import uniq from 'lodash/uniq';
import { cloneDeep, isEqual, isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  anyLlmModels,
  type BlurOptions,
  type Node as WasmNode,
  type NodeId,
  type Channel,
  type Input,
  type Edit,
  type Web,
  type InputId,
  type OutputId,
  DataType,
  VideoState,
  CompositorState,
  NodeKind,
  AiModelKind,
  ParameterValue,
  Parameter,
  Vec2,
  PainterState,
  ListSelector,
  EvaluatedValue,
  MaskExtractor,
  KlingElement,
  Wildcard,
  AiModelInfo,
  Deprecated,
  Cursor,
  NodeType,
  ModelNodeType,
  VideoToLoad,
  ImageToLoad,
  ThreeDModelToLoad,
  FontToLoad,
} from 'web';
import {
  validateAiModel,
  validateDataType,
  validateFile,
  validateInputs,
  validateOutputs,
  validateParameters,
  validateParameterValue,
  validateString,
  validateStringArray,
} from '@/utils/nodeInputValidation';
import { hexToRgbaObject } from '@/utils/color.utils';
import { log } from '@/logger/logger';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { HandleType } from '@/enums/handle-type.enum';
import { ModelAssetToUploadedAsset, UploadedAsset, UploadedAssetToModelAsset } from '@/types/api/assets';
import { containFileDataWithoutId } from '@/utils/nodeDataValidator';
import { NodeType as NodeTypeEnum } from '@/enums/node-type.enum';
import { migratePromptV2ToPromptV3 } from '@/utils/node-migrations-utils';
import {
  removeBlockedAnyLlmModelsIfNeeded,
  shouldBlockAnyLlmModelIfNeeded as shouldBlockAnyLlmModel,
} from '@/utils/ModelProviders/blockedAnyLlmModels.utils';
import { getHandleType } from '../Nodes/DynamicNode/HandlesUtils';
import { WasmAPI, WasmErrorType } from './WasmAPI';
import { loadFontResources, loadImageResources, loadThreeDModelResources, loadVideoResources } from './resourceLoading';
import { UndoRedo, UndoRedoEntry } from './undoRedo';
import { FlowState, FlowStore } from './FlowStore';
import { mergeSeedParam } from './Views/ModelBaseView';
import { LEGACY_FONT_SIZE_TO_SIZE } from './Views/StickyNoteView';
import type { DuplicateNodeOptions } from './flowTypes';
import type {
  Node,
  Edge,
  Handle,
  EdgeId,
  TextData,
  NumberData,
  BaseNodeData,
  ExtractVideoFrameData,
  PreviewData,
  PromptData,
  PromptConcatenatorData,
  ListSelectorData,
  NumberSelectorData,
  StringArrayData,
  ImportData,
  ExportData,
  FigmaCompositorData,
  WorkflowOutputData,
  ToggleData,
  SeedData,
  RouterData,
  MediaIteratorData,
  ImportLoraData,
  ComparisonData,
  PointCloudFromDepthData,
  StickyNoteData,
} from '@/types/node';
import type { CompositorNodeV3 } from '@/types/nodes/compositor';
import type { AiModelData, ModelBaseNodeData } from '@/types/nodes/model';

export type FlowErrorType = WasmErrorType;
export type FlowWarningType =
  | 'failed-deserialize-3d-model'
  | 'failed-deserialize-font'
  | 'failed-deserialize-image'
  | 'failed-deserialize-video'
  | 'failed-download-image'
  | 'failed-download-video'
  | 'failed-download-font'
  | 'failed-download-3d-model';

export interface FlowUIState {
  compositor: Record<NodeId, CompositorState>;
  video: Record<NodeId, VideoState[]>;
  painter: Record<NodeId, PainterState>;
  cursor: Record<NodeId, Cursor>;
}

const initialUIState: FlowUIState = {
  compositor: {},
  video: {},
  painter: {},
  cursor: {},
};

function isSupportedNodeType(nodeType: unknown): nodeType is NodeType {
  const supportedNodeTypes = [
    'compv3',
    'channels',
    'blur',
    'invert',
    'merge_alpha',
    'levels',
    'dilation_erosion',
    'string',
    'integer',
    'promptV3',
    'promptV2',
    'prompt_concat',
    'array',
    'export',
    'workflow_output',
    'boolean',
    'seed',
    'extract_video_frame',
    'preview',
    'router',
    'custommodelV2',
    'import',
    'media_iterator',
    'mux',
    'muxv2',
    'number_selector',
    'resize',
    'crop',
    'ImportLoRA',
    'multilora',
    'painterV2',
    'masks',
    'edit',
    'compv2',
    'deprecated',
    'comparison',
    'point_cloud_from_depth',
    'kling_element',
    'prompt_enhance',
    'kling',
    'sd_bgrmv',
    'sd_inpaint',
    'sd_outpaint',
    'ig_text2image',
    'br_text2image',
    'minimax',
    'mochiv1',
    'rw_video',
    'objectremove',
    'nim_cc',
    'any_llm',
    'sd_img2video',
    'sd_sketch',
    'sd_text2image',
    'sd_upscale',
    'br_vector',
    'dalle3',
    'flux_fast',
    'flux_lora',
    'flux_pro',
    'ig_describe',
    'image2image',
    'luma_video',
    'meshy_image23d',
    'minimax_i2v',
    'custommodel',
    'figma_compositor',
    'wildcardV2',
  ];

  return supportedNodeTypes.includes(nodeType as NodeType);
}

let instance: FlowGraph | undefined;
const logger = log.getLogger('FlowGraph');

export class FlowGraph {
  private flowStore: FlowStore;
  private wasmAPI: WasmAPI;
  private eventHandler: EventHandler;

  private undoRedo = new UndoRedo();
  private ongoingUndoState: UndoRedoEntry | undefined;
  private errorListener: (errorType: FlowErrorType) => void;
  private warningListener: (warningType: FlowWarningType) => void;

  private uiState: FlowUIState = initialUIState;
  private uiStateListeners = new Set<() => void>();

  private nodeInputListeners = new Map<NodeId, Set<() => void>>();

  /** Presigned font URLs from the workflow fonts hook, keyed by `originalFileName` (graph font key). */
  private workspaceCustomFontsUrlsMap: ReadonlyMap<string, string> = new Map();

  private updateQueue: string[] = [];
  private unsub = () => {};

  /** Edge ids the user explicitly selected in React Flow (marquee / click); merged with incident highlighting in {@link recomputeEdgeSelection}. */
  private explicitEdgeIds = new Set<EdgeId>();

  constructor(
    nodes: Node[],
    edges: Edge[],
    canvas: HTMLCanvasElement,
    editable: boolean,
    errorListener: (errorType: FlowErrorType) => void,
    warningListener: (warningType: FlowWarningType) => void,
    workspaceCustomFontsUrlsMap: ReadonlyMap<string, string> = new Map(),
  ) {
    if (instance) {
      logger.error('FlowGraph instance already exists');
      instance.dispose();
    }
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    instance = this;

    this.errorListener = errorListener;
    this.warningListener = warningListener;
    this.workspaceCustomFontsUrlsMap = workspaceCustomFontsUrlsMap;

    this.flowStore = new FlowStore({ nodes, edges });

    const unsubNodes = this.store.addNodesListener(() => this.onStateChange());
    const unsubEdges = this.store.addEdgesListener(() => this.onStateChange());
    this.unsub = () => {
      unsubNodes();
      unsubEdges();
    };

    this.wasmAPI = new WasmAPI(canvas, this.store, editable, {
      onError: (errorType: WasmErrorType) => {
        this.errorListener(errorType);
        if (errorType === 'recovered') {
          this.loadNodes();
          this.uiState = initialUIState;
        }
      },
      updateUIState: (updater) => this.updateUIState(updater),
      pushUndoState: (undoState) => this.undoRedo.pushEntry(undoState),
      updateLegacy: (nodeId, isSeek) => {
        if (!isSeek || this.store.getNodeNullable(nodeId)?.type === 'extract_video_frame') {
          this.updateNodeOutputs(nodeId);
        }
      },
    });

    this.eventHandler = new EventHandler(this, canvas);

    // load nodes
    this.loadNodes();
  }

  get store() {
    return this.flowStore;
  }

  get wasm() {
    return this.wasmAPI;
  }

  private isSupportedNode(nodeId: NodeId): boolean {
    const type = this.getNodeType(nodeId);
    return isSupportedNodeType(type);
  }

  private logInvalidNodeData(nodes: { id: NodeId; node: Node }[]) {
    try {
      // iterate over all nodes and check if they contain file data object without id
      nodes.forEach((node) => {
        const invalidNode = containFileDataWithoutId(node.node.data, node.id);
        if (invalidNode) {
          logger.warn('AssetIdValidationError: during graph load, missing id in node', invalidNode);
        }
      });
    } catch (error) {
      logger.warn('Error logging invalid node data', error);
    }
  }

  private loadNodes() {
    for (const node of this.store.getNodes()) {
      // The 4th reference image handle was removed from KlingElement; migrate old workflows that still have it
      if (node.type === 'kling_element') {
        const handles = node.data.handles.input;
        if (!Array.isArray(handles) && handles['reference_image_url4']) {
          delete handles['reference_image_url4'];
          const edgeIdsToRemove = this.store
            .getEdges()
            .filter((edge) => edge.target === node.id && edge.targetHandle?.includes('reference_image_url4'))
            .map((edge) => edge.id);
          if (edgeIdsToRemove.length > 0) {
            this.pruneExplicitEdgeIds(edgeIdsToRemove);
            this.store.removeEdges(edgeIdsToRemove);
          }
        }
      }
      if (node.type === 'crop') {
        if (Array.isArray(node.data.handles.input) && node.data.handles.input.includes('image')) {
          for (const edge of this.store.getEdges()) {
            if (edge.target === node.id && edge.targetHandle) {
              edge.targetHandle = edge.targetHandle.replace('image', 'file');
            }
          }
          node.data.handles.input = ['file'];
        }
        if (Array.isArray(node.data.handles.output) && node.data.handles.output.includes('result')) {
          for (const edge of this.store.getEdges()) {
            if (edge.source === node.id && edge.sourceHandle) {
              edge.sourceHandle = edge.sourceHandle.replace('result', 'file');
            }
          }
          node.data.handles.output = ['file'];
        }
      }
      if (node.type === 'prompt_enhance') {
        if (Array.isArray(node.data.handles.output) && node.data.handles.output.includes('text')) {
          node.data.handles.output = ['enhanced_prompt'];
          for (const edge of this.store.getEdges()) {
            if (edge.source === node.id && edge.sourceHandle) {
              edge.sourceHandle = edge.sourceHandle.replace('text', 'enhanced_prompt');
            }
          }
        }
      }
      if (node.type === 'stickynote') {
        const stickyData = node.data as StickyNoteData;
        if (stickyData.fontSize) {
          stickyData.size = LEGACY_FONT_SIZE_TO_SIZE.get(stickyData.fontSize);
          stickyData.fontSize = undefined;
        }
      }
    }

    const wasmNodeEntries = this.toWasmNodes(this.store.getNodes());

    this.logInvalidNodeData(this.store.getNodes().map((n) => ({ id: n.id, node: n })));

    this.wasmChange((wasm) => {
      return wasm.importGraph({ nodes: new Map(wasmNodeEntries.map(({ id, node }) => [id, node])) });
    });

    this.updateNodeInputs(wasmNodeEntries.map(({ id }) => id));
    this.redraw();
    this.loadResources();
  }

  dispose() {
    this.eventHandler.dispose();
    this.wasmAPI.dispose();
    this.unsub();
    instance = undefined;
  }

  getUIState() {
    return this.uiState;
  }

  addUIStateListener(f: () => void) {
    this.uiStateListeners.add(f);
    return () => {
      this.uiStateListeners.delete(f);
    };
  }

  private updateUIState(updater: (oldState: FlowUIState) => Partial<FlowUIState>) {
    this.uiState = {
      ...this.uiState,
      ...updater(this.uiState),
    };
    for (const listener of this.uiStateListeners) {
      listener();
    }
  }

  addNodeInputListener(nodeId: NodeId, f: () => void) {
    if (!this.nodeInputListeners.has(nodeId)) {
      this.nodeInputListeners.set(nodeId, new Set());
    }
    const nodeInputListeners = this.nodeInputListeners.get(nodeId)!;
    nodeInputListeners.add(f);
    return () => {
      nodeInputListeners.delete(f);
      if (nodeInputListeners.size === 0) {
        this.nodeInputListeners.delete(nodeId);
      }
    };
  }

  private notifyNodeInputListeners(nodeId: NodeId) {
    for (const listener of this.nodeInputListeners.get(nodeId) ?? []) {
      listener();
    }
  }

  private onStateChange() {
    const nodeIdToUpdate = this.updateQueue.pop();
    if (nodeIdToUpdate == null) return;
    this.propagateOutput(nodeIdToUpdate);
  }

  private getState(): FlowState {
    return this.store.getState();
  }

  undo() {
    const undoEntry = this.undoRedo.undo();
    if (undoEntry) {
      const redoEntry: UndoRedoEntry = this.applyUndoRedoEntry(undoEntry);
      this.undoRedo.pushRedo(redoEntry);
    }
  }

  redo() {
    const redoEntry = this.undoRedo.redo();
    if (redoEntry) {
      const undoEntry: UndoRedoEntry = this.applyUndoRedoEntry(redoEntry);
      this.undoRedo.pushUndo(undoEntry);
    }
  }

  hasUndo() {
    return this.undoRedo.hasUndo();
  }

  hasRedo() {
    return this.undoRedo.hasRedo();
  }

  setUndoBarrier() {
    return this.undoRedo.setUndoBarrier();
  }

  disableUndoBarrier() {
    return this.undoRedo.disableBarrier();
  }

  cancelOngoingAction() {
    const undoState = this.ongoingUndoState;
    if (undoState !== undefined) {
      this.applyUndoRedoEntry(undoState, true);
      this.ongoingUndoState = undefined;
    }
  }

  edit(apply: () => UndoRedoEntry, ongoing: boolean = false) {
    this.cancelOngoingAction();
    const undoEntry = apply();
    if (ongoing) {
      this.ongoingUndoState = undoEntry;
    } else {
      this.undoRedo.pushEntry(undoEntry);
    }
  }

  wasmEdit(apply: (wasm: Web) => Edit, ongoing: boolean = false) {
    this.edit(() => this.wasmChange(apply), ongoing);
  }

  wasmChange(apply: (wasm: Web) => Edit): UndoRedoEntry {
    const undoState = this.wasm.edit(apply);
    return UndoRedoEntry.fromWasmEdit(undoState);
  }

  clearUndoRedoHistory() {
    this.undoRedo.clear();
  }

  private applyUndoRedoEntry(undoRedoEntry: UndoRedoEntry, ongoing: boolean = false): UndoRedoEntry {
    if (!undoRedoEntry.changes) return UndoRedoEntry.empty();

    const reverseEntry = UndoRedoEntry.empty();
    const nodeIdsToUpdate: NodeId[] = [];

    for (const change of undoRedoEntry.changes.toReversed()) {
      if (change.type === 'node') {
        const nodeEntry = change.entry;
        if (nodeEntry.type === 'remove') {
          reverseEntry.add(this.store.addNodes([nodeEntry.node]));
        } else if (nodeEntry.type === 'add') {
          reverseEntry.add(this.store.removeNodes([nodeEntry.nodeId]));
        } else if (nodeEntry.type === 'update') {
          reverseEntry.add(this.store.setNode(nodeEntry.node.id, nodeEntry.node));
          nodeIdsToUpdate.push(nodeEntry.node.id);
        } else if (nodeEntry.type === 'position') {
          reverseEntry.add(this.store.setNodePosition(nodeEntry.nodeId, nodeEntry.position));
        }
      }

      if (change.type === 'edge') {
        const edgeEntry = change.entry;
        if (edgeEntry.type === 'remove') {
          reverseEntry.add(this.store.addEdges([edgeEntry.edge]));
          nodeIdsToUpdate.push(edgeEntry.edge.target);
        } else if (edgeEntry.type === 'add') {
          const edge = this.store.getEdge(edgeEntry.edgeId);
          this.pruneExplicitEdgeIds([edgeEntry.edgeId]);
          reverseEntry.add(this.store.removeEdges([edgeEntry.edgeId]));
          nodeIdsToUpdate.push(edge.target);
        }
      }

      if (change.type === 'wasm') {
        const editState = change.entry;
        reverseEntry.add(this.wasmChange((wasm) => ({ editState, undoState: wasm.edit(editState) })));
        nodeIdsToUpdate.push(...editState.map((e) => e.nodeId));
      }
    }

    if (!ongoing) {
      const existingNodeIdsToUpdate = nodeIdsToUpdate.filter((id) => this.store.getNodeNullable(id) !== undefined);
      this.updateNodeInputs(existingNodeIdsToUpdate, true);
      this.redraw();
      this.loadResources();
    }

    return reverseEntry;
  }

  addNodes(newNodes: Node[]): UndoRedoEntry {
    const undoEntry = this.store.addNodes(newNodes);

    const nodeEntries = this.toWasmNodes(newNodes);
    if (nodeEntries.length === 0) return undoEntry;
    undoEntry.add(this.wasmChange((wasm) => wasm.insertNodes(nodeEntries)));

    return undoEntry;
  }

  // remove nodes and associated edges
  removeNodes(nodeIdsToRemove: NodeId[]): UndoRedoEntry {
    const { edges } = this.getState();

    const removedEdges = edges.filter((e) => nodeIdsToRemove.includes(e.source) || nodeIdsToRemove.includes(e.target));
    const undoEntry = this.removeEdges(removedEdges.map((e) => e.id));

    // remove only nodes loaded in wasm
    const wasmNodeIdsToRemove = nodeIdsToRemove.filter((id) => this.isSupportedNode(id));
    undoEntry.add(this.wasmChange((wasm) => wasm.removeNodes(wasmNodeIdsToRemove)));
    undoEntry.add(this.store.removeNodes(nodeIdsToRemove));

    this.loadResources();

    return undoEntry;
  }

  /**
   * Like {@link removeNodes}, but adds edges from each predecessor outside the removed set
   * to each successor outside the set whenever they were connected only through removed nodes.
   */
  removeNodesPreservingBridgingConnections(nodeIdsToRemove: NodeId[]): UndoRedoEntry {
    const { edges } = this.getState();
    const removedSet = new Set(nodeIdsToRemove);

    const bridgeEdges = this.computeBridgeEdgesForRemovedNodes(edges, removedSet);

    const removedEdges = edges.filter((e) => nodeIdsToRemove.includes(e.source) || nodeIdsToRemove.includes(e.target));
    const undoEntry = this.removeEdges(removedEdges.map((e) => e.id));

    if (bridgeEdges.length > 0) {
      undoEntry.add(this.addEdges(bridgeEdges));
    }

    const wasmNodeIdsToRemove = nodeIdsToRemove.filter((id) => this.isSupportedNode(id));
    undoEntry.add(this.wasmChange((wasm) => wasm.removeNodes(wasmNodeIdsToRemove)));
    undoEntry.add(this.store.removeNodes(nodeIdsToRemove));

    this.loadResources();

    return undoEntry;
  }

  private computeBridgeEdgesForRemovedNodes(edges: Edge[], removedSet: Set<NodeId>): Edge[] {
    const bridges: Edge[] = [];
    const bridgeAndSurvivorKeys = new Set<string>();

    const connectionKey = (source: NodeId, sourceHandle: string, target: NodeId, targetHandle: string) =>
      `${source}\0${sourceHandle}\0${target}\0${targetHandle}`;

    /** Inputs that will still have an incoming edge after removed nodes (and their incident edges) are gone. */
    const occupiedTargetInputKeys = new Set<string>();

    for (const e of edges) {
      if (!removedSet.has(e.source) && !removedSet.has(e.target)) {
        if (e.sourceHandle != null && e.targetHandle != null) {
          bridgeAndSurvivorKeys.add(connectionKey(e.source, e.sourceHandle, e.target, e.targetHandle));
        }
      }
      if (e.targetHandle != null && !removedSet.has(e.target) && !removedSet.has(e.source)) {
        occupiedTargetInputKeys.add(`${e.target}\0${e.targetHandle}`);
      }
    }

    const outgoingBySource = new Map<NodeId, Edge[]>();
    for (const e of edges) {
      const list = outgoingBySource.get(e.source);
      if (list) {
        list.push(e);
      } else {
        outgoingBySource.set(e.source, [e]);
      }
    }
    for (const list of outgoingBySource.values()) {
      list.sort((a, b) => {
        const ta = `${a.target}\0${a.targetHandle ?? ''}\0${a.id}`;
        const tb = `${b.target}\0${b.targetHandle ?? ''}\0${b.id}`;
        return ta.localeCompare(tb);
      });
    }

    const tryAddBridge = (source: NodeId, sourceHandle: string, target: NodeId, targetHandle: string) => {
      if (source === target) {
        return;
      }
      const targetInputKey = `${target}\0${targetHandle}`;
      if (occupiedTargetInputKeys.has(targetInputKey)) {
        return;
      }
      if (!this.isBridgingConnectionTypeCompatible(source, sourceHandle, target, targetHandle)) {
        return;
      }
      const k = connectionKey(source, sourceHandle, target, targetHandle);
      if (bridgeAndSurvivorKeys.has(k)) {
        return;
      }
      bridgeAndSurvivorKeys.add(k);
      occupiedTargetInputKeys.add(targetInputKey);
      bridges.push(this.createEdge(source, target, sourceHandle, targetHandle));
    };

    const dfs = (extSource: NodeId, extSourceHandle: string, currentNode: NodeId, visitedInPath: Set<NodeId>) => {
      if (visitedInPath.has(currentNode)) {
        return;
      }
      visitedInPath.add(currentNode);
      for (const out of outgoingBySource.get(currentNode) ?? []) {
        if (out.targetHandle == null) {
          continue;
        }
        if (removedSet.has(out.target)) {
          dfs(extSource, extSourceHandle, out.target, visitedInPath);
        } else {
          tryAddBridge(extSource, extSourceHandle, out.target, out.targetHandle);
        }
      }
      visitedInPath.delete(currentNode);
    };

    const entryEdges = edges.filter(
      (e) => !removedSet.has(e.source) && removedSet.has(e.target) && e.sourceHandle != null,
    );
    entryEdges.sort((a, b) =>
      `${a.source}\0${a.sourceHandle}\0${a.id}`.localeCompare(`${b.source}\0${b.sourceHandle}\0${b.id}`),
    );

    for (const e of entryEdges) {
      dfs(e.source, e.sourceHandle!, e.target, new Set());
    }

    return bridges;
  }

  /**
   * True if a wire from {@link source}'s output handle to {@link target}'s input handle is type-valid.
   */
  private isBridgingConnectionTypeCompatible(
    source: NodeId,
    sourceHandle: string,
    target: NodeId,
    targetHandle: string,
  ): boolean {
    let outputId: OutputId;
    let inputId: InputId;
    try {
      outputId = this.outputKey({ source, sourceHandle, target: source, targetHandle: sourceHandle } as Edge);
    } catch {
      return false;
    }
    try {
      inputId = this.inputKey({ source: target, sourceHandle: targetHandle, target, targetHandle } as Edge);
    } catch {
      return false;
    }

    const outType = this.nodeOutputType(source, outputId);
    const validTypes = this.nodeValidInputTypes(target, inputId);

    if (validTypes.length > 0 && outType !== undefined) {
      return validTypes.includes(outType);
    }

    const sourceNode = this.store.getNodeNullable(source);
    const targetNode = this.store.getNodeNullable(target);
    if (!sourceNode || !targetNode) {
      return false;
    }
    const wireOut = getHandleType(sourceHandle, sourceNode);
    const wireIn = getHandleType(targetHandle, targetNode);
    return wireOut === wireIn || wireOut === HandleType.Any || wireIn === HandleType.Any;
  }

  wouldConnectionResultInCycle(source: NodeId, target: NodeId): boolean {
    const edges = this.store.getEdges();
    const hasCycle = (nodeId: NodeId, visited = new Set<NodeId>()) => {
      if (nodeId === source || visited.has(nodeId)) {
        return true;
      }
      visited.add(nodeId); // To handle existing cycles

      for (const targetId of edges.filter((edge) => edge.source === nodeId).map((edge) => edge.target)) {
        if (hasCycle(targetId, new Set(visited))) {
          return true;
        }
      }

      return false;
    };

    return hasCycle(target);
  }

  /**
   * Return true if the input of a given node input handle is valid.
   */
  isInputValid(targetNodeId: NodeId, targetInputId: InputId): boolean {
    return this.wasm.call((wasm) => wasm.isInputValid(targetNodeId, targetInputId)) ?? false;
  }

  /**
   * Return the connected node and output id of a given node input handle
   * or undefined if the input handle is not connected.
   */
  getNodeInput(nodeId: NodeId, inputId: InputId): Input | undefined {
    return this.wasm.call((wasm) => wasm.nodeInput(nodeId, inputId));
  }

  /**
   * Return the ids of the inputs of a node in the correct order.
   */
  getInputIds(nodeId: NodeId): InputId[] {
    return this.wasm.call((wasm) => wasm.inputIds(nodeId)) ?? [];
  }

  /**
   * Return the ids of the outputs of a node in the correct order.
   */
  getOutputIds(nodeId: NodeId): OutputId[] {
    return this.wasm.call((wasm) => wasm.outputIds(nodeId)) ?? [];
  }

  /**
   * Return the valid types of input of a given node input handle.
   */
  nodeValidInputTypes(nodeId: NodeId, inputId: InputId): Array<HandleType> {
    const types = this.wasm.call((wasm) => wasm.nodeValidInputTypes(nodeId, inputId))?.map(dataTypeToHandleType) ?? [];
    if (types.includes(HandleType.Image) || types.includes(HandleType.Video)) {
      return [...types, HandleType.Mask];
    }
    return types;
  }

  /**
   * Return true if the input of a given node input handle is required.
   */
  isInputRequired(nodeId: NodeId, inputId: InputId): boolean {
    return this.wasm.call((wasm) => wasm.isInputRequired(nodeId, inputId)) ?? false;
  }

  /**
   * Return the type of input of a given node input handle.
   */
  nodeInputType(nodeId: NodeId, inputId: InputId): HandleType | undefined {
    const type = this.wasm.call((wasm) => wasm.nodeInputType(nodeId, inputId));
    if (type === 'image' || type === 'video') {
      const handle = this.store.getNodeDataNullable(nodeId)?.handles?.input[inputId];
      if (handle?.type === HandleType.Mask) {
        return HandleType.Mask;
      }
    }
    return type ? dataTypeToHandleType(type) : undefined;
  }

  /**
   * Return the type of output of a given node output handle.
   */
  nodeOutputType(nodeId: NodeId, outputId: OutputId): HandleType | undefined {
    const type = this.wasm.call((wasm) => wasm.nodeOutputType(nodeId, outputId));
    if (type === 'image' || type === 'video') {
      const handle = this.store.getNodeDataNullable(nodeId)?.handles?.output[outputId];
      if (handle?.type === HandleType.Mask) {
        return HandleType.Mask;
      }
    }
    return type ? dataTypeToHandleType(type) : HandleType.Any;
  }

  /**
   * Evaluate the output of a given node output handle.
   */
  evaluateOutput(input: Input): EvaluatedValue | undefined {
    return this.wasm.call((wasm) => wasm.evaluateOutput(input));
  }

  /**
   * Evaluate the input of a given node input handle.
   */
  evaluateInput(nodeId: NodeId, inputId: InputId): EvaluatedValue | undefined {
    return this.wasm.call((wasm) => wasm.evaluateInput(nodeId, inputId));
  }

  /**
   * Evaluate the data needed for displaying on the node.
   * **Warning: Only use this when evaluating the data needed to be displayed on the node!**
   * If you need the output or input of a node, use the functions {@link FlowGraph.evaluateOutput} or {@link FlowGraph.evaluateInput} instead.
   *
   * This is sometimes the same as the output of the only output handle of the node.
   * Example: An array node displays the array on the node and outputs the same array.
   * However, the output of the node is often also different from the data we display on the node.
   * Example: A list selector node displays the entire list of options on the node but only outputs the selected option.
   */
  evaluateNode(nodeId: NodeId, index?: number): EvaluatedValue | undefined {
    return this.wasm.call((wasm) => wasm.evaluateNode(nodeId, index));
  }

  addEdges(newEdges: Edge[]): UndoRedoEntry {
    if (newEdges.length === 0) return UndoRedoEntry.empty();

    const validEdges = newEdges.filter((edge) => !this.wouldConnectionResultInCycle(edge.source, edge.target));
    const inputs = validEdges.map((edge) => {
      let input: Input | undefined = undefined;
      const targetInputId: InputId = this.inputKey(edge);

      const outputKey = this.outputKey(edge);
      input = {
        nodeId: edge.source,
        outputId: outputKey,
        file: undefined,
        string: undefined,
        stringArray: undefined,
      };

      return {
        targetNodeId: edge.target,
        targetInputId,
        input,
      };
    });

    const undoState: UndoRedoEntry = this.store.addEdges(validEdges);

    for (const { targetNodeId, targetInputId, input } of inputs) {
      if (input === undefined) {
        continue;
      }
      undoState.add(this.wasmChange((wasm) => wasm.setNodeInput(targetNodeId, targetInputId, input)));
    }

    this.updateNodeInputs(newEdges.map((e) => e.target));
    this.redraw();
    this.loadVideoResources();

    return undoState;
  }

  removeEdges(edgeIds: EdgeId[]): UndoRedoEntry {
    if (edgeIds.length === 0) return UndoRedoEntry.empty();

    this.pruneExplicitEdgeIds(edgeIds);

    const dependentNodeIds: NodeId[] = edgeIds.map((edgeId) => this.store.getEdge(edgeId).target);
    const undoState = UndoRedoEntry.empty();
    const inputs: { nodeId: NodeId; inputId: InputId }[] = edgeIds.map((edgeId) => {
      const edge = this.store.getEdge(edgeId);
      return {
        nodeId: edge.target,
        inputId: this.inputKey(edge),
      };
    });
    undoState.add(this.store.removeEdges(edgeIds));

    for (const { nodeId, inputId } of inputs) {
      undoState.add(this.wasmChange((wasm) => wasm.setNodeInput(nodeId, inputId, null)));
    }

    this.updateNodeInputs(dependentNodeIds);
    this.redraw();
    this.loadVideoResources();

    return undoState;
  }

  getSelectedNodes(): NodeId[] {
    return this.getState()
      .nodes.filter((n) => n.selected)
      .map((n) => n.id);
  }

  setSelectedNodes(selectedNodeIds: NodeId[]) {
    const selectedIdSet = new Set(selectedNodeIds);
    for (const node of this.store.getNodes()) {
      const selected = selectedIdSet.has(node.id);
      if (Boolean(node.selected) !== selected) {
        this.store.updateNode(node.id, () => ({ selected }));
      }
    }

    this.recomputeEdgeSelection();
  }

  private pruneExplicitEdgeIds(edgeIds: EdgeId[]): void {
    if (edgeIds.length === 0) return;
    for (const id of edgeIds) {
      this.explicitEdgeIds.delete(id);
    }
  }

  /**
   * Sets `edge.selected` when the edge is incident to a selected node or explicitly selected in React Flow.
   * Order-independent: explicit ids are kept in {@link explicitEdgeIds}.
   */
  private recomputeEdgeSelection(): void {
    const nodeIdSet = new Set(this.getSelectedNodes());
    for (const edge of this.store.getEdges()) {
      // edge is selected if it is explicitly selected or part-of-path to selected nodes
      const selected = this.explicitEdgeIds.has(edge.id) || nodeIdSet.has(edge.source) || nodeIdSet.has(edge.target);
      if (Boolean(edge.selected) !== selected) {
        this.store.updateEdge(edge.id, () => ({ selected }));
      }
    }
  }

  getExplicitSelectedEdgeIds(): EdgeId[] {
    return Array.from(this.explicitEdgeIds);
  }

  getSelectedEdges(): EdgeId[] {
    return this.store
      .getEdges()
      .filter((e) => e.selected)
      .map((e) => e.id);
  }

  setSelectedEdges(edgeIds: EdgeId[]) {
    this.explicitEdgeIds = new Set(edgeIds);
    this.recomputeEdgeSelection();
  }

  addNodeInputHandle(nodeId: NodeId, key: string, handle: Handle) {
    return this.store.updateNodeData(nodeId, (nodeData) => ({
      handles: {
        ...nodeData.handles,
        input: { ...nodeData.handles.input, [key]: handle },
      },
    }));
  }

  updateNodeData<T extends BaseNodeData>(nodeId: NodeId, newData: Partial<T>): UndoRedoEntry {
    // schedule update for downstream nodes
    if ('output' in newData) {
      this.updateQueue.push(nodeId);
    }

    return this.store.updateNodeData(nodeId, () => ({ ...newData }));
  }

  /**
   * User-facing rename: updates `data.name`. When `originalName` is still missing (e.g. new node before save, or
   * pre-migration payload), sets it from the previous `data.name` once. Loaded recipes get `originalName` from
   * the API read-time migration when omitted in storage, so this path often only runs for unsaved graph edits.
   */
  renameNodeDisplayName(nodeId: NodeId, newName: string): UndoRedoEntry | undefined {
    const trimmed = newName.trim();
    if (!trimmed) {
      return undefined;
    }
    const node = this.store.getNodeNullable(nodeId);
    if (!node || node.type === NodeTypeEnum.CustomGroup) {
      return undefined;
    }
    if (trimmed === node.data.name) {
      return undefined;
    }
    return this.store.updateNode(nodeId, (node) => {
      const next: Partial<Node> = {
        data: { ...node.data, name: trimmed },
      };
      if (node.originalName === undefined) {
        next.originalName = node.data.name;
      }
      return next;
    });
  }

  updateModelNodeData<T extends ModelBaseNodeData>(nodeId: NodeId, newData: Partial<T>): UndoRedoEntry {
    // schedule update for downstream nodes
    if ('output' in newData) {
      this.updateQueue.push(nodeId);
    }

    return this.store.updateNodeData(nodeId, () => ({ ...newData }));
  }

  isNodeInGroup(nodeId: NodeId): boolean {
    const node = this.store.getNodeNullable(nodeId);
    if (!node) return false;
    return !!node.parentId;
  }

  toggleNodeLocked(nodeId: NodeId): UndoRedoEntry {
    const undoEntry = this.store.updateNode(nodeId, (node) => {
      // Toggle the node's own isLocked state (not the effective state)
      // The effective state is used for display/behavior, but we toggle the actual property
      const isLocked = !node.data.isLocked;
      return {
        draggable: isLocked ? false : undefined,
        data: {
          ...node.data,
          isLocked: isLocked,
        },
      };
    });
    if (this.isSupportedNode(nodeId)) {
      undoEntry.add(this.wasmChange((wasm) => wasm.toggleNodeLocked(nodeId)));
    }
    return undoEntry;
  }

  setNodeParent(nodeId: NodeId, parentId: NodeId | undefined): UndoRedoEntry {
    if (parentId && this.getNodeType(parentId) !== ('custom_group' as NodeType)) {
      throw new Error('parent must be a group');
    }
    if (this.isSupportedNode(nodeId)) {
      return this.wasmChange((wasm) => wasm.setNodeParent(nodeId, parentId));
    } else {
      return this.store.updateNode(nodeId, () => ({ parentId }));
    }
  }

  getChildNodesOfGroup(groupId: string): Node[] {
    return this.store.getNodes().filter((node) => node.parentId === groupId);
  }

  /**
   * Gets the effective lock state of a node, considering parent group lock state.
   * If the node is in a locked group, returns true. Otherwise returns the node's own lock state.
   */
  getEffectiveIsLocked(nodeId: NodeId): boolean {
    const node = this.store.getNodeNullable(nodeId);
    if (!node) {
      return false;
    }

    if (node.type === NodeTypeEnum.CustomGroup) {
      return node.data.isLocked ?? false;
    }

    // Check if node has a parent group
    if (node.parentId) {
      const parentNode = this.store.getNodeNullable(node.parentId);
      // If parent exists and is locked, return its lock state
      if (parentNode && parentNode.data.isLocked) {
        return true;
      }
    }

    // Return the node's own lock state
    return node.data.isLocked ?? false;
  }

  /**
   * @deprecated Do not use or change! Can be removed when there are no usages left of the deprecated input/output data.
   */
  private getNodeOutput(nodeId: NodeId, outputKey: string) {
    const sourceNode = this.store.getNodeNullable(nodeId);
    if (!sourceNode) return undefined;
    return sourceNode.data.output
      ? typeof sourceNode.data.output === 'object' && outputKey in sourceNode.data.output
        ? sourceNode.data.output[outputKey]
        : sourceNode.data.output
      : undefined;
  }

  /**
   * @deprecated Do not use or change! Can be removed when there are no usages left of the deprecated input/output data.
   */
  private getNodeOutputValue(nodeId: NodeId, outputKey: OutputId): Omit<Input, 'nodeId' | 'outputId'> {
    const value: unknown = this.getNodeOutput(nodeId, outputKey);
    return {
      file: validateFile(value),
      string: validateString(value),
      stringArray: validateStringArray(value),
    };
  }

  /**
   * @deprecated Do not use or change! Can be removed when there are no usages left of the deprecated input/output data.
   */
  updateNodeInputs(nodeIds: NodeId[], propagateModelOutputs: boolean = false) {
    for (const nodeId of uniq(nodeIds)) {
      const node = this.store.getNodeNullable(nodeId);
      if (!node) continue; // node might have been removed since this is function is async

      if (this.isSupportedNode(nodeId)) {
        const inputIds = this.wasm.call((wasm) => wasm.inputIds(nodeId)) ?? [];
        for (const inputId of inputIds) {
          const input: Input | undefined = this.wasm.call((wasm) => wasm.nodeInput(nodeId, inputId));
          if (input) {
            const inputValue = this.getNodeOutputValue(input.nodeId, input.outputId);
            if (
              !isEqual(inputValue.file, input.file) ||
              !isEqual(inputValue.stringArray, input.stringArray) ||
              inputValue.string !== input.string
            ) {
              this.wasm.edit((wasm) => {
                return wasm.updateNodeInput(
                  nodeId,
                  inputId,
                  inputValue?.file,
                  inputValue?.string,
                  inputValue?.stringArray,
                );
              });
            }
          }
        }
        this.notifyNodeInputListeners(nodeId);

        const type = this.getNodeType(nodeId);
        if (type !== 'custommodelV2' || propagateModelOutputs) {
          // If it's a model node, the output will not change if the input changes
          this.updateNodeOutputs(nodeId);
        }
      }
    }
  }

  async loadResourcesAsync() {
    const imagesToLoad = this.wasmAPI.call((wasm) => wasm.imagesToLoad()) ?? [];
    const videosToLoad = this.wasmAPI.call((wasm) => wasm.videosToLoad()) ?? [];
    const threeDModelsToLoad = this.wasmAPI.call((wasm) => wasm.threeDModelsToLoad()) ?? [];
    const fontsToLoad = this.wasmAPI.call((wasm) => wasm.fontsToLoad()) ?? [];

    await Promise.all([
      this.loadImageResourcesInternal(imagesToLoad),
      this.loadVideoResourcesInternal(videosToLoad),
      this.loadThreeDModelResourcesInternal(threeDModelsToLoad),
      this.loadFontResourcesInternal(fontsToLoad),
    ]);

    this.wasm.call((wasm) => wasm.loadMaskResources());
  }

  loadResources() {
    const imagesToLoad = this.wasmAPI.call((wasm) => wasm.imagesToLoad()) ?? [];
    const videosToLoad = this.wasmAPI.call((wasm) => wasm.videosToLoad()) ?? [];
    const threeDModelsToLoad = this.wasmAPI.call((wasm) => wasm.threeDModelsToLoad()) ?? [];
    const fontsToLoad = this.wasmAPI.call((wasm) => wasm.fontsToLoad()) ?? [];

    void Promise.all([
      this.loadImageResourcesInternal(imagesToLoad),
      this.loadVideoResourcesInternal(videosToLoad),
      this.loadThreeDModelResourcesInternal(threeDModelsToLoad),
      this.loadFontResourcesInternal(fontsToLoad),
    ]);

    this.wasm.call((wasm) => wasm.loadMaskResources());
  }

  loadImageResources() {
    const imagesToLoad = this.wasmAPI.call((wasm) => wasm.imagesToLoad()) ?? [];
    void this.loadImageResourcesInternal(imagesToLoad);
  }

  private async loadImageResourcesInternal(imagesToLoad: ImageToLoad[]) {
    await loadImageResources(
      imagesToLoad.map((image) => image.key),
      (url, img) => {
        if (img) {
          const nodeIds = imagesToLoad.find((v) => v.key === url)?.nodeIds ?? [];
          try {
            this.wasmAPI.call((wasm) => wasm.addImageResource(url, img, nodeIds));
            for (const nodeId of nodeIds) {
              this.updateNodeOutputs(nodeId, false);
            }
          } catch (e) {
            logger.error('Error deserializing image resource: ', e);
            this.warningListener('failed-deserialize-image');
          }
        } else {
          this.warningListener('failed-download-image');
        }
      },
    );
  }

  loadVideoResources() {
    const videosToLoad = this.wasmAPI.call((wasm) => wasm.videosToLoad()) ?? [];
    void this.loadVideoResourcesInternal(videosToLoad);
  }

  private async loadVideoResourcesInternal(videosToLoad: VideoToLoad[]) {
    await loadVideoResources(
      videosToLoad.map((video) => video.key),
      (url, video) => {
        if (video) {
          const nodeIds = videosToLoad.find((v) => v.key === url)?.nodeIds ?? [];
          try {
            this.wasmAPI.call((wasm) => wasm.addVideoResource(url, video, nodeIds));
            for (const nodeId of nodeIds) {
              this.updateNodeOutputs(nodeId, false);
            }
          } catch (e) {
            logger.error('Error deserializing video resource: ', e);
            this.warningListener('failed-deserialize-video');
          }
        } else {
          this.warningListener('failed-download-video');
        }
      },
    );
  }

  loadThreeDModelResources() {
    const threeDModelsToLoad = this.wasmAPI.call((wasm) => wasm.threeDModelsToLoad()) ?? [];
    void this.loadThreeDModelResourcesInternal(threeDModelsToLoad);
  }

  private async loadThreeDModelResourcesInternal(threeDModelsToLoad: ThreeDModelToLoad[]) {
    await loadThreeDModelResources(
      threeDModelsToLoad.map((threeDModel) => threeDModel.key),
      (key, modelBytes) => {
        if (modelBytes) {
          const nodeIds = threeDModelsToLoad.find((v) => v.key === key)?.nodeIds ?? [];
          try {
            this.wasmAPI.call((wasm) => wasm.addThreeDModelResource(key, modelBytes, nodeIds));
            for (const nodeId of nodeIds) {
              this.updateNodeOutputs(nodeId, false);
            }
          } catch (e) {
            logger.error('Error deserializing threeDModel resource: ', e);
            this.warningListener('failed-deserialize-3d-model');
          }
        } else {
          this.warningListener('failed-download-3d-model');
        }
      },
    );
  }

  /**
   * @param workspaceCustomFontsUrlsMap When provided, applied before loading; otherwise the map from the constructor is used.
   */
  loadFontResources(workspaceCustomFontsUrlsMap?: ReadonlyMap<string, string>) {
    if (workspaceCustomFontsUrlsMap !== undefined) {
      this.workspaceCustomFontsUrlsMap = workspaceCustomFontsUrlsMap;
    }
    const fontResourceKeysToLoad = this.wasmAPI.call((wasm) => wasm.fontsToLoad()) ?? [];
    void this.loadFontResourcesInternal(fontResourceKeysToLoad);
  }

  private async loadFontResourcesInternal(fontsToLoad: FontToLoad[]) {
    await loadFontResources(
      fontsToLoad.map((font) => font.key),
      (key, fontBytes) => {
        if (fontBytes) {
          const nodeIds = fontsToLoad.find((v) => v.key === key)?.nodeIds ?? [];
          try {
            this.wasmAPI.call((wasm) => wasm.addFontResource(key, fontBytes, nodeIds));
            for (const nodeId of nodeIds) {
              this.updateNodeOutputs(nodeId, false);
            }
          } catch (e) {
            logger.error('Error deserializing font resource: ', e);
            this.warningListener('failed-deserialize-font');
          }
        } else {
          this.warningListener('failed-download-font');
        }
      },
      this.workspaceCustomFontsUrlsMap,
    );
  }

  private redraw() {
    this.wasmAPI.call((wasm) => {
      wasm.redraw();
    });
  }

  /**
   * @deprecated Do not use or change! Can be removed when there are no usages left of the deprecated input/output data.
   */
  updateNodeOutputs(nodeId: NodeId, ongoing: boolean = false, propagateOutput: boolean = true) {
    if (!this.store.getNodeNullable(nodeId)) return;

    if (ongoing) {
      // Don't propagate the result if ongoing
      return;
    }

    // This function should only be called for supported node types
    if (!this.isSupportedNode(nodeId)) return;
    // Save the result to the node data (can be removed when no node depends on this)
    const type = this.getNodeType(nodeId);
    switch (type) {
      case 'blur':
      case 'merge_alpha':
      case 'channels':
      case 'invert':
      case 'compv3':
      case 'levels':
      case 'dilation_erosion':
      case 'resize':
      case 'crop':
      case 'extract_video_frame': {
        let outputId;
        switch (type) {
          case 'blur': {
            outputId = 'result';
            break;
          }
          case 'merge_alpha': {
            outputId = 'image_with_alpha';
            break;
          }
          case 'channels': {
            outputId = 'matte';
            break;
          }
          case 'invert': {
            outputId = 'result';
            break;
          }
          case 'compv3': {
            outputId = 'image';
            break;
          }
          case 'levels': {
            outputId = 'result';
            break;
          }
          case 'dilation_erosion': {
            outputId = 'result';
            break;
          }
          case 'resize': {
            outputId = 'result';
            break;
          }
          case 'crop': {
            outputId = 'file';
            break;
          }
          case 'extract_video_frame': {
            outputId = 'result';
            break;
          }
          default: {
            const _exhaustiveCheck: never = type;
          }
        }
        const img = this.wasmAPI.call((wasm) => {
          return wasm.exportNodeRender(nodeId, outputId, 2048);
        });
        const result = img
          ? { type: 'image' as const, url: img.data, width: img.width, height: img.height }
          : undefined;
        this.store.updateNodeData(nodeId, (_) => {
          return {
            result,
            output: {
              [outputId]: result,
            },
          };
        });
        break;
      }
      case 'painterV2': {
        const img = this.wasmAPI.call((wasm) => {
          return wasm.exportNodeRender(nodeId, 'result', 2048);
        });
        const result = img
          ? { type: 'image' as const, url: img.data, width: img.width, height: img.height }
          : undefined;

        const maskImg = this.wasmAPI.call((wasm) => {
          return wasm.exportNodeRender(nodeId, 'mask', 2048);
        });
        const mask = maskImg
          ? { type: 'image' as const, url: maskImg.data, width: maskImg.width, height: maskImg.height }
          : undefined;
        this.store.updateNodeData(nodeId, (_) => {
          return {
            output: {
              result,
              mask,
            },
          };
        });
        break;
      }
      case 'masks': {
        const img = this.wasmAPI.call((wasm) => {
          return wasm.exportNodeRender(nodeId, 'image_with_alpha');
        });
        const image_with_alpha = img
          ? { type: 'image' as const, url: img.data, width: img.width, height: img.height }
          : undefined;

        const maskImg = this.wasmAPI.call((wasm) => {
          return wasm.exportNodeRender(nodeId, 'mask');
        });
        const mask = maskImg
          ? { type: 'image' as const, url: maskImg.data, width: maskImg.width, height: maskImg.height }
          : undefined;
        this.store.updateNodeData(nodeId, (_) => {
          return {
            output: {
              image_with_alpha,
              mask,
            },
          };
        });
        break;
      }
      case 'string': {
        const string = this.store.getNodeData<TextData>(nodeId).value;
        this.store.updateNodeData(nodeId, (_) => {
          return {
            result: {
              string,
            },
            output: {
              type: 'text',
              text: string,
              string, // hotfix - some old nodes has a string output handle
            },
          };
        });
        break;
      }
      case 'multilora': {
        const { loras, selectedIndex, weight } = this.store.getNodeData<ImportLoraData>(nodeId);
        const lora = loras[selectedIndex];
        this.store.updateNodeData<ImportLoraData>(nodeId, (_) => {
          return {
            result: { lora: lora?.file, selectedLora: lora, weight: weight },
            selectedLora: lora,
            output: {
              type: 'lora',
              loRA_URL: lora ? `weavy-lora::${lora.file}` : undefined,
              weight: weight,
            },
          };
        });
        break;
      }
      case 'promptV3': {
        const prompt = this.store.getNodeData<PromptData>(nodeId).prompt;
        const evaluatedValue = this.evaluateNode(nodeId);
        const populatedPrompt = evaluatedValue?.type === 'string' ? evaluatedValue.value : prompt;

        this.store.updateNodeData<PromptData>(nodeId, (_) => {
          return {
            result: { prompt: populatedPrompt },
            output: {
              type: 'text',
              prompt: populatedPrompt,
            },
          };
        });
        break;
      }
      case 'integer': {
        const number = this.store.getNodeData<NumberData>(nodeId);
        this.store.updateNodeData(nodeId, (_) => {
          return {
            result: number.value,
            output: {
              type: number.mode,
              number: number.value,
            },
          };
        });
        break;
      }
      case 'muxv2': {
        const evaluatedValue = this.wasm.call((wasm) =>
          wasm.evaluateOutput({
            nodeId,
            outputId: 'option',
            file: undefined,
            string: undefined,
            stringArray: undefined,
          }),
        );
        if (evaluatedValue && evaluatedValue.type !== 'string') throw new Error('expected string output from muxv2');

        const value = this.evaluateNode(nodeId);
        const options = value?.type === 'string_array' ? value.value : [];
        const option = evaluatedValue?.value;
        this.store.updateNodeData<ListSelectorData>(nodeId, (_) => {
          return {
            result: option,
            output: {
              type: 'text',
              option,
            },
            // FIXME: server-side executor expects options to be in params.options
            params: { options },
          };
        });
        break;
      }
      case 'number_selector': {
        const { selected, params, inputNode } = this.store.getNodeData<NumberSelectorData>(nodeId);
        const options = inputNode?.stringArray?.map((s) => parseFloat(s)).filter((n) => !isNaN(n)) ?? params.options;
        this.store.updateNodeData<NumberSelectorData>(nodeId, (_) => {
          return {
            result: selected,
            output: {
              type: 'number',
              option: selected !== undefined ? options[selected] : undefined,
            },
          };
        });
        break;
      }
      case 'array': {
        this.store.updateNodeData<StringArrayData>(nodeId, ({ inputNode, array, delimiter }) => {
          const newArray = inputNode?.string ? inputNode.string.split(delimiter) : array;
          return {
            result: newArray,
            output: { type: 'array', array: newArray },
          };
        });
        break;
      }
      case 'boolean': {
        const boolean = this.store.getNodeData<ToggleData>(nodeId);
        this.store.updateNodeData(nodeId, (_) => {
          return {
            result: boolean.value,
            output: {
              type: 'boolean',
              option: boolean.value,
            },
          };
        });
        break;
      }
      case 'media_iterator': {
        const { selectedIndex } = this.store.getNodeData<MediaIteratorData>(nodeId);
        const files = this.wasmAPI.call((wasm) => {
          return wasm.evaluateParameter(nodeId, 'files');
        });

        const file = files?.value?.[selectedIndex];
        this.store.updateNodeData<MediaIteratorData>(nodeId, (_) => {
          return {
            result: file,
            output: {
              ['file']: file,
            },
          };
        });
        break;
      }
      case 'import': {
        const { files, selectedIndex } = this.store.getNodeData<ImportData>(nodeId);
        const file = files[selectedIndex];
        let image: { type: 'image'; url: string; width: number; height: number } | undefined = undefined;
        if (file?.type === '3D') {
          const img = this.wasmAPI.call((wasm) => {
            return wasm.exportNodeRender(nodeId, undefined, 1024);
          });
          image = img ? { type: 'image' as const, url: img.data, width: img.width, height: img.height } : undefined;
        }
        this.store.updateNodeData<ImportData>(nodeId, (_) => {
          return {
            result: file,
            output: {
              ['file']: file,
              ['image']: image,
            },
          };
        });
        break;
      }
      case 'seed': {
        const seedData = this.store.getNodeData<SeedData>(nodeId);
        this.store.updateNodeData(nodeId, (_) => {
          const result = {
            isRandom: seedData.isRandom,
            seed: seedData.seed,
          };
          return {
            result,
            ...result,
            output: {
              type: 'seed',
              seed: {
                ...result,
              },
            },
          };
        });
        break;
      }
      case 'router': {
        const { inputNode } = this.store.getNodeData<RouterData>(nodeId);
        const inputValue = inputNode ? this.getNodeOutputValue(inputNode.nodeId, inputNode.outputId) : undefined;
        const result = inputValue?.file ?? inputValue?.string ?? inputValue?.stringArray;
        this.store.updateNodeData<RouterData>(nodeId, (_) => {
          return {
            result,
            output: {
              out: result,
            },
          };
        });
        break;
      }
      case 'comparison': {
        const { inputA, inputB } = this.store.getNodeData<ComparisonData>(nodeId);
        const inputValueA = inputA ? this.getNodeOutputValue(inputA.nodeId, inputA.outputId) : undefined;
        const inputValueB = inputB ? this.getNodeOutputValue(inputB.nodeId, inputB.outputId) : undefined;
        const outA = inputValueA?.file ?? inputValueA?.string ?? inputValueA?.stringArray;
        const outB = inputValueB?.file ?? inputValueB?.string ?? inputValueB?.stringArray;
        this.store.updateNodeData<ComparisonData>(nodeId, (_) => ({ output: { out_a: outA, out_b: outB } }));
        break;
      }
      case 'export':
      case 'deprecated':
      case 'preview':
      case 'point_cloud_from_depth':
      case 'figma_compositor':
      case 'kling_element':
      case 'workflow_output': {
        return;
      }
      case 'prompt_concat': {
        const additionalPrompt = this.store.getNodeData<PromptConcatenatorData>(nodeId).additionalPrompt;
        const inputNodes = this.store.getNodeData<PromptConcatenatorData>(nodeId).inputNodes;
        this.store.updateNodeData<PromptConcatenatorData>(nodeId, (_) => {
          return {
            result: { additionalPrompt },
            output: {
              type: 'text',
              prompt:
                inputNodes
                  .map(([_, input]) => input?.string)
                  .filter((s) => s !== undefined)
                  .join(' ') + (additionalPrompt ? ' ' + additionalPrompt : ''),
            },
          };
        });
        break;
      }
      case 'custommodelV2': {
        const { generations, selectedIndex } = this.store.getNodeData<AiModelData>(nodeId);
        const result = generations.map(ModelAssetToUploadedAsset);
        const file = result[selectedIndex];
        let image: { type: 'image'; url: string; width: number; height: number } | undefined = undefined;
        if (file?.type === '3D') {
          const img = this.wasmAPI.call((wasm) => {
            return wasm.exportNodeRender(nodeId, undefined, 1024);
          });
          image = img ? { type: 'image' as const, url: img.data, width: img.width, height: img.height } : undefined;
        }
        const outputId =
          this.wasmAPI.call((wasm) => {
            return wasm.outputIds(nodeId);
          })?.[0] ?? 'result';
        this.store.updateNodeData<AiModelData>(nodeId, (_) => {
          return {
            result,
            output: file?.type === '3D' ? { [outputId]: file, ['image']: image } : { [outputId]: file },
            selectedOutput: selectedIndex,
          };
        });
        break;
      }
      default: {
        const _exhaustiveCheck: never = type;
      }
    }

    // Propagate the result to downstream nodes
    if (propagateOutput) {
      this.propagateOutput(nodeId);
    }
  }

  /**
   * @deprecated Do not use or change!
   */
  private propagateOutput(nodeId: NodeId) {
    const dependentNodeIds = this.store
      .getEdges()
      .filter((edge) => edge.source === nodeId)
      .map((edge) => edge.target);
    this.updateNodeInputs(dependentNodeIds);
  }

  duplicateNodesAndEdges(
    nodes: Node[],
    edges: Edge[],
    options?: DuplicateNodeOptions,
  ): { undoEntry: UndoRedoEntry; newNodeIds: NodeId[] } {
    const { newNodes, idMapping } = this.duplicateNodes(nodes);
    const newEdges = this.duplicateEdges(edges, idMapping, options?.preserveExternalInputs ?? false);

    const undoEntry = UndoRedoEntry.empty();
    // Add new nodes
    undoEntry.add(this.addNodes(newNodes));
    const newNodeIds = newNodes.map((n) => n.id);

    // Remove all connections from new nodes
    for (const nodeId of newNodeIds) {
      if (this.isSupportedNode(nodeId)) {
        for (const inputId of this.wasm.call((wasm) => wasm.inputIds(nodeId)) ?? []) {
          undoEntry.add(this.wasmChange((wasm) => wasm.setNodeInput(nodeId, inputId, null)));
        }
      }
    }
    // Add new edges
    undoEntry.add(this.addEdges(newEdges));

    // Load resources and update legacy node inputs
    this.updateNodeInputs(newNodeIds);
    this.redraw();
    this.loadResources();
    return { undoEntry, newNodeIds };
  }

  createEdge(source: NodeId, target: NodeId, sourceHandle: string, targetHandle: string): Edge {
    const sourceNode = this.store.getNode(source);
    const targetNode = this.store.getNode(target);
    return {
      id: uuidv4(),
      source,
      target,
      sourceHandle,
      targetHandle,
      type: 'custom',
      data: {
        sourceColor: sourceNode?.data?.color,
        targetColor: targetNode?.data?.color,
        sourceHandleType: getHandleType(sourceHandle, sourceNode),
        targetHandleType: getHandleType(targetHandle, targetNode),
      },
    };
  }

  private duplicateNodes(clipboardNodes: Node[]): { newNodes: Node[]; idMapping: Record<string, string> } {
    const idMapping: Record<string, string> = {};
    const newNodes = cloneDeep(clipboardNodes).map((node) => {
      const newNodeId = uuidv4();
      idMapping[node.id] = newNodeId;
      return {
        ...node,
        id: newNodeId,
        data: {
          ...node.data,
          // TODO: when save is split to single actions - move this generation to backend
          // when duplicating a node
          result: Array.isArray(node.data.result)
            ? node.data.result.map((r: any) => {
                if (r !== null && r !== undefined && typeof r === 'object' && !Array.isArray(r)) {
                  return { ...r, id: crypto.randomUUID() };
                }
                return r;
              })
            : node.data.result,
        },
        createdAt: new Date().toISOString(),
      };
    });

    newNodes.forEach((node) => {
      if (node.parentId) {
        if (idMapping[node.parentId]) {
          node.parentId = idMapping[node.parentId];
        } else {
          const parentNode = this.store.getNodeNullable(node.parentId);
          if (!parentNode) {
            node.parentId = undefined;
          }
        }
      }
    });

    return { newNodes, idMapping };
  }

  private duplicateEdges(
    clipboardEdges: Edge[],
    idMapping: Record<string, string>,
    preserveExternalInputs: boolean,
  ): Edge[] {
    return cloneDeep(clipboardEdges).reduce((acc: Edge[], edge) => {
      const newSource = idMapping[edge.source];
      const newTarget = idMapping[edge.target];

      if (newSource && newTarget) {
        acc.push({
          ...edge,
          id: uuidv4(),
          source: newSource,
          target: newTarget,
          sourceHandle: edge.sourceHandle?.replace(edge.source, newSource),
          targetHandle: edge.targetHandle?.replace(edge.target, newTarget),
        });
        return acc;
      }

      if (preserveExternalInputs && !newSource && newTarget) {
        acc.push({
          ...edge,
          id: uuidv4(),
          source: edge.source,
          target: newTarget,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle?.replace(edge.target, newTarget),
        });
      }

      return acc;
    }, []);
  }

  private inputKey(edge: Edge): string {
    const inputKey = edge.targetHandle?.split('-input-')?.[1];
    if (!inputKey) throw new Error('Expected input key');
    return inputKey;
  }

  private outputKey(edge: Edge): string {
    const outputKey = edge.sourceHandle?.split('-output-')?.[1];
    if (!outputKey) throw new Error('Expected output key');
    return outputKey;
  }

  private toWasmNodes(nodes: Node[]): { id: string; node: WasmNode }[] {
    return nodes
      .filter((n) => n.type !== 'custom_group' && n.type !== 'stickynote' && n.type !== 'target')
      .map((n) => {
        const wasmNode = this.toWasmNode(n);
        return wasmNode ? { id: n.id, node: wasmNode } : null;
      })
      .filter((n) => n != null);
  }

  // Migration
  private toWasmNode(node: Node): WasmNode | null {
    const nodeId = node.id;
    if (!this.isSupportedNode(nodeId)) {
      logger.warn(
        `Node is not supported on the new architecture: ${node.type}` +
          (node.data?.model ? `(model: ${JSON.stringify(node.data.model)})` : ''),
      );
      return null;
    }

    const input: Map<InputId, Input | undefined> = new Map();
    const handles = node.data.handles.input || [];
    for (const inputId of Array.isArray(handles) ? handles : Object.keys(handles)) {
      const edge = this.store.getEdges().find((e) => e.target === nodeId && this.inputKey(e) === inputId);
      if (edge && this.store.getNodeNullable(edge.source) !== undefined) {
        input.set(inputId, {
          nodeId: edge.source,
          outputId: this.outputKey(edge),
          file: undefined,
          string: undefined,
          stringArray: undefined,
        });
      } else {
        input.set(inputId, undefined);
      }
    }

    // remove kind from node
    delete (node as { kind?: object }).kind;

    // Handle deprecated node types
    if (node.type === 'compv2') {
      node.type = 'deprecated';
      const data = {
        type: 'compv2',
        inputs: validateInputs(node.data.handles?.input, undefined).map((i) => [i.id, input.get(i.id)]),
      } satisfies Deprecated;
      node.data = { ...node.data, ...data };
    }
    if (node.type === 'edit') {
      node.type = 'deprecated';
      const data = {
        type: 'edit',
        inputNode: input.get('file'),
      } satisfies Deprecated;
      node.data = { ...node.data, ...data };
    }

    if (node.type === 'ImportLoRA') {
      node.type = 'multilora';
      const data = node.data as ImportLoraData;
      data.loras =
        data.result && typeof data.result === 'object' && 'lora' in data.result && typeof data.result.lora === 'string'
          ? [
              {
                id: uuidv4(),
                file: data.result.lora,
                name: 'Untitled',
                coverImage: undefined,
                defaultWeight: 0.5,
                trigger: '',
              },
            ]
          : [];
      data.selectedIndex = 0;
      data.weight = 0.0;
      data.viewMode = NodeViewMode.Single;
      for (const edge of this.store.getEdges()) {
        if (edge.source === node.id && edge.sourceHandle) {
          edge.sourceHandle = edge.sourceHandle.replace('lora', 'loRA_URL');
        }
      }
      data.handles.output = ['loRA_URL'];
      node.data = data;
    }

    if (node.type === 'wildcardV2') {
      (node.data as any).isWildCard = true;
      node.type = 'custommodelV2';
    }

    // change legacy model nodes to custommodelV2 type
    if (
      node.type === 'prompt_enhance' ||
      node.type === 'kling' ||
      node.type === 'sd_bgrmv' ||
      node.type === 'sd_inpaint' ||
      node.type === 'sd_outpaint' ||
      node.type === 'ig_text2image' ||
      node.type === 'br_text2image' ||
      node.type === 'minimax' ||
      node.type === 'mochiv1' ||
      node.type === 'rw_video' ||
      node.type === 'objectremove' ||
      node.type === 'nim_cc' ||
      node.type === 'any_llm' ||
      node.type === 'sd_img2video' ||
      node.type === 'sd_sketch' ||
      node.type === 'sd_text2image' ||
      node.type === 'sd_upscale' ||
      node.type === 'br_vector' ||
      node.type === 'dalle3' ||
      node.type === 'flux_fast' ||
      node.type === 'flux_lora' ||
      node.type === 'flux_pro' ||
      node.type === 'ig_describe' ||
      node.type === 'image2image' ||
      node.type === 'luma_video' ||
      node.type === 'meshy_image23d' ||
      node.type === 'minimax_i2v' ||
      node.type === 'custommodel'
    ) {
      node.type = 'custommodelV2';
    }
    // migrate promptv2 to promptv3
    if (node.type === 'promptV2') {
      migratePromptV2ToPromptV3(
        node,
        (edgeIds: EdgeId[]) => {
          this.pruneExplicitEdgeIds(edgeIds);
          return this.store.removeEdges(edgeIds);
        },
        () => this.store.getEdges(),
      );
    }
    // migrate mux to muxv2
    if (node.type === 'mux') {
      node.type = 'muxv2';
      const data = node.data as ListSelectorData;

      // move `options` into `list`
      if (Array.isArray(data.options)) {
        data.list = [...data.options];
      } else {
        data.list = [];
      }
      data.params = { options: [...data.list] };
      data.schema = {
        options: {
          type: 'array',
          order: 0,
          title: 'Options',
          exposed: true,
          required: false,
          description: 'Array of options to choose from',
        },
      };

      data.options = undefined;
      data.delimiter = ',';
      data.isIterator = false;

      if (data.result !== undefined) {
        const index = (data.list ?? []).findIndex((s) => s === data.result);
        data.selected = index === -1 ? 0 : index;
      } else {
        data.selected = 0;
      }

      // save current output handle name
      const outputHandleName = Array.isArray(data.handles.output)
        ? data.handles.output[0]
        : Object.keys(data.handles.output ?? {})[0];

      // object handles
      data.handles = {
        input: {
          options: {
            id: 'd83d338b-f6c1-4ff2-a160-e81d918995fc',
            type: HandleType.Array,
            label: 'Options',
            format: 'array',
            required: false,
            order: 0,
            description: 'Array of options to choose from',
          },
        },
        output: {
          option: {
            id: 'xK9mP2vR8nL4jH7tF5wQ123',
            type: HandleType.Text,
            label: 'Text',
            order: 0,
            format: 'text',
            description: 'The selected option',
            required: false,
          },
        },
      };
      data.version = 3;

      // edges
      if (outputHandleName) {
        // rename outgoing edge
        for (const edge of this.store.getEdges()) {
          if (edge.source === node.id && edge.sourceHandle) {
            edge.sourceHandle = edge.sourceHandle.replace(outputHandleName, 'option');
          }
        }
      }
    }

    const migrateParameter = (data: any, key: string, defaultValue: ParameterValue): Parameter => {
      const property = data.schema[key];
      if (property === undefined || property === null) {
        logger.error(
          `Parameter ${key} is not defined in the schema for model ${data?.model?.name} in node ${nodeId}. It has value ${data.params[key]}`,
        );
        return {
          type: 'value',
          data: defaultValue,
        };
      }
      if (property.exposed) {
        return {
          type: 'input',
          data: input.get(key),
        };
      }
      return {
        type: 'value',
        data: validateParameterValue(data.params[key], property.default, property) ?? defaultValue,
      };
    };

    const migrateWildcard = (data: any, modelInfo: AiModelInfo) => {
      if (modelInfo.type === 'none' && data.model?.name && data.model.name !== '') {
        logger.error(`Failed to migrate model ${data.model.name} to wildcard: ${JSON.stringify(data.model)}`);
      }
      const hasModel = modelInfo.type !== 'none';
      const wildcard = {
        model: modelInfo,
        inputs: hasModel ? validateInputs(data.handles?.input, data.schema).map((input) => [input, undefined]) : [],
        parameters: hasModel
          ? validateParameters(data.handles?.input, data.params, data.schema).map((parameter) => [
              parameter,
              { type: 'value', data: parameter.defaultValue },
            ])
          : [],
        outputs: hasModel ? validateOutputs(data.handles?.output, data.schema) : [],
      } satisfies Wildcard;
      data.kind = {
        type: 'wildcard',
        ...wildcard,
      } satisfies AiModelKind;

      for (const inputData of data.kind.inputs) {
        inputData[1] = input.get(inputData[0].id);
      }
      for (const parameterData of data.kind.parameters) {
        parameterData[1] = migrateParameter(data, parameterData[0].id, parameterData[0].defaultValue);
      }
    };

    const type = this.getNodeType(nodeId);

    let data;
    switch (type) {
      case 'compv3': {
        const compositorData = (node.data as CompositorNodeV3).data;
        data = {
          ...compositorData,
          duration: compositorData.duration ?? 5.0,
          fps: compositorData.fps ?? 24,
          isTimelineEnabled:
            (isNil(compositorData.isTimelineEnabled) &&
              Object.values(compositorData.layers).some(
                (layer) => layer.kind.type === 'video' || layer.kind.type === 'audio',
              )) ||
            Boolean(compositorData.isTimelineEnabled),
          layers: new Map(
            Object.entries(compositorData.layers).map(([key, layer]) => {
              if (layer.kind.inputId === undefined) {
                layer.kind.inputId = (layer.kind as unknown as { input_id: string }).input_id;
              }
              if (typeof layer.kind.inputId === 'number') {
                if (Array.isArray(handles)) {
                  throw new Error('Expected object handles');
                }
                const inputId = Object.entries(handles).find(
                  (v) => String(v[1].order) === String(layer.kind.inputId),
                )?.[0];
                if (!inputId) {
                  throw new Error('Expected valid input handle string key when migrating compositor node');
                }
                layer.kind.inputId = inputId;
              }
              if (layer.kind.type === 'video' || layer.kind.type === 'audio') {
                layer.kind.trimStart = layer.kind.trimStart ?? 0;
                layer.kind.trimEnd = layer.kind.trimEnd ?? 0;
                layer.kind.muted = layer.kind.muted ?? false;
              }
              layer.startTime = layer.startTime ?? 0;
              layer.duration = layer.duration ?? 5;
              layer.name = layer.name ?? layer.kind.inputId;
              return [Number(key), layer];
            }),
          ),
          input: Array.from(input.entries()),
        };
        break;
      }
      case 'channels': {
        const c = (node.data as { channel?: number | Channel }).channel;
        let channel: Channel;
        if (c === undefined || c === 1) {
          channel = 'red';
        } else if (c === 2) {
          channel = 'green';
        } else if (c === 3) {
          channel = 'blue';
        } else {
          channel = c as Channel;
        }
        data = { channel, inputNode: input.get('map') };
        break;
      }
      case 'blur': {
        let blurData = (node.data as any).options;
        if (blurData === undefined) {
          blurData = (node.data as any).blurData ?? { size: 3, type: 'Box' };
          if (blurData.type === 1) blurData.type = 'Box';
          if (blurData.type === 2) blurData.type = 'Gaussian';
          blurData.size = Number(blurData.size);
          blurData = blurData as BlurOptions;
        }
        data = { options: blurData, inputNode: input.get('file') };
        break;
      }
      case 'invert': {
        data = { inputNode: input.get('file') };
        break;
      }
      case 'merge_alpha': {
        data = { inputNode: input.get('image'), alphaInput: input.get('alpha_image') };
        break;
      }
      case 'levels': {
        const levelsOptions = (node.data as any).options;
        data = { options: levelsOptions, inputNode: input.get('file') };
        break;
      }
      case 'dilation_erosion': {
        data = node.data;
        data.inputNode = input.get('input');
        break;
      }
      case 'resize': {
        const resize_properties = (node.data as any).resize_properties;
        data = {
          inputNode: input.get('image'),
          options:
            'options' in node.data
              ? node.data.options
              : resize_properties?.resize
                ? {
                    lockAspectRatio: resize_properties?.lockAspectRatio ?? true,
                    width: resize_properties.resize.width ?? 1024,
                    height: resize_properties.resize.height ?? 1024,
                  }
                : undefined,
        };

        if (data.options) {
          const MAX_DIMENSION = 8192;
          const { width, height } = data.options;
          const aspectRatio = width / height;
          if (width > height && width > MAX_DIMENSION) {
            data.options.width = MAX_DIMENSION;
            data.options.height = Math.round(MAX_DIMENSION / aspectRatio);
          } else if (height >= width && height > MAX_DIMENSION) {
            data.options.width = Math.round(MAX_DIMENSION * aspectRatio);
            data.options.height = MAX_DIMENSION;
          }
        }

        break;
      }
      case 'crop': {
        const { crop_data, lockAspectRatio } = node.data as any;
        const scaleFactor = crop_data?.scaleFactor ?? 1;
        const options = crop_data
          ? {
              x: Math.ceil((crop_data.x ?? 0) * scaleFactor),
              y: Math.ceil((crop_data.y ?? 0) * scaleFactor),
              width: Math.ceil((crop_data.width ?? 1) * scaleFactor),
              height: Math.ceil((crop_data.height ?? 1) * scaleFactor),
              lockAspectRatio: lockAspectRatio ?? true,
            }
          : undefined;

        data = {
          inputNode: input.get('file') ?? input.get('image'),
          options: 'options' in node.data ? node.data.options : options,
        };
        break;
      }
      case 'extract_video_frame': {
        data = node.data as ExtractVideoFrameData;
        if (data.time === undefined || data.time === null) {
          if (data.options?.time !== undefined && data.options.time !== null) {
            data.time = data.options.time;
          } else {
            const fps = Math.max(1, Math.round(data.input?.file?.fps ?? 30));
            const frame = Math.max(0, Math.round(data.frameNumber ?? 0));
            data.time = (frame + 0.5) / fps;
          }
        }
        data.inputNode = input.get('file');
        break;
      }
      case 'string': {
        data = node.data as TextData;
        if (data.value === undefined) {
          data = { value: data.result?.string ?? data.output?.['text'] ?? '', ...data };
        }
        break;
      }
      case 'promptV3': {
        data = node.data as PromptData;
        if (data.prompt === undefined) {
          data = { prompt: data.result?.prompt ?? data.output?.['prompt'] ?? '', ...data };
        }

        for (const inputNode of data.inputNodes ?? []) {
          inputNode[1] = input.get(inputNode[0]);
        }
        break;
      }
      case 'muxv2': {
        data = node.data as ListSelectorData;
        if (data.delimiter === undefined) {
          const isIterator = data.isIterator ?? false;
          const options = data.params?.options ?? [];
          let exposed = data.schema?.options?.exposed ?? false;
          if (data.parameter === undefined && data.schema?.options !== undefined) {
            // For old text iterator data, the exposed flag is not reliable
            if (this.store.getEdges().some((edge) => edge.target === nodeId)) {
              exposed = true;
            } else if (options.length > 0) {
              exposed = false;
            }
            data.schema.options.exposed = exposed;
          }
          if (!exposed) {
            data.handles.input = {};
          } else {
            data.handles.input = {
              options: {
                id: 'd83d338b-f6c1-4ff2-a160-e81d918995fc',
                type: HandleType.Array,
                label: 'Options',
                format: 'array',
                required: false,
                order: 0,
                description: 'Array of options to choose from',
              },
            };
          }

          data = {
            selected: isIterator ? 0 : (data.result ?? 0),
            isIterator,
            options: exposed
              ? { type: 'input', data: input.get('options') }
              : {
                  type: 'value',
                  data: { type: 'string_array', value: options.map((opt) => (opt != undefined ? opt.toString() : '')) },
                },
          };

          // migration to revert the above and add a delimiter:
          data = {
            options: input.get('options'),
            list: data.options.type === 'value' ? data.options.data.value : [],
            selected: data.selected,
            isIterator: data.isIterator,
            delimiter: ',',
          } satisfies ListSelector;
        } else {
          data.options = input.get('options');
        }
        break;
      }
      case 'number_selector': {
        data = node.data as NumberSelectorData;
        data = {
          selected: data.result ?? 0,
          params: data.params && data.params.options ? { options: data.params.options } : { options: [] },
          inputNode: input.get('options'),
        };
        break;
      }
      case 'integer': {
        data = node.data as NumberData;
        data = {
          value: data.value ?? data.result ?? data.output?.number ?? 0,
          mode: data.mode ?? 'integer',
          min: typeof data.min === 'number' ? data.min : undefined,
          max: typeof data.max === 'number' ? data.max : undefined,
        };
        break;
      }
      case 'array': {
        data = node.data as StringArrayData;
        if (data.inputNode === undefined) {
          data = {
            array: Array.isArray(data?.array) ? data.array : Array.isArray(data?.result) ? data.result : [],
            delimiter: typeof data?.delimiter === 'string' ? data.delimiter : '*',
            inputNode: input.get('text'),
          };
        } else {
          data.inputNode = input.get('text');
        }

        break;
      }
      case 'preview': {
        data = node.data as PreviewData;
        data.inputNode = input.get('file');
        break;
      }
      case 'media_iterator': {
        data = node.data as MediaIteratorData;
        if (data.acceptedFileType === undefined) {
          data.acceptedFileType = 'image';
        }
        break;
      }
      case 'import': {
        data = node.data as ImportData;
        if (data.files === undefined) {
          const validFile = validateFile(data.result) ?? validateFile(data.output?.file) ?? validateFile(data.value);
          data.files = validFile ? [validFile] : [];
        }

        if (data.cameraLocked === undefined) {
          data.cameraLocked = data.cameraOptions?.locked ?? data.is3DLocked ?? false;
        }

        const cameraPosition = data.cameraOptions?.position ?? data.cameraPosition;
        if (
          cameraPosition != null &&
          cameraPosition.x != null &&
          cameraPosition.y != null &&
          cameraPosition.z != null
        ) {
          data.files = data.files.map((file) => {
            if (file.type === '3D' && file.cameraOptions === undefined) {
              return {
                ...file,
                cameraOptions: {
                  position: cameraPosition,
                  target: undefined,
                  up: { x: 0, y: 1, z: 0 },
                },
              };
            }
            return file;
          });
        }

        if (data.selectedIndex === undefined) {
          data.selectedIndex = data.files.length > 0 ? data.files.length - 1 : 0;
        }

        if (data.files?.length) {
          for (const file of data.files) {
            if (file.type === 'audio') {
              if (isNil(file.duration)) {
                // This is a fallback for missing durations. Real migration is done on backend.
                // Before this - we didn't save duration on audio files
                file.duration = 5;
              }
            }
          }
        }
        break;
      }
      case 'export': {
        data = node.data as ExportData;
        data.inputNode = input.get('file');
        break;
      }
      case 'figma_compositor': {
        data = node.data as FigmaCompositorData;
        const existingInputs = data.inputs ?? [];
        const figmaCompositorInputs = Array.from(input.entries()).map(([inputId, connection]) => {
          const existing = existingInputs.find(([id]) => id === inputId);
          return [
            inputId,
            {
              figmaNodeId: existing?.[1]?.figmaNodeId,
              validTypes: existing?.[1]?.validTypes,
              connection,
            },
          ];
        });

        data.inputs = figmaCompositorInputs;
        break;
      }
      case 'workflow_output': {
        data = node.data as WorkflowOutputData;
        data.inputNode = input.get('workflow');
        break;
      }
      case 'boolean': {
        data = node.data as ToggleData;
        if (data.value === undefined) {
          data.value = data.result ?? data.output?.option ?? false;
        }
        break;
      }
      case 'seed': {
        data = node.data as SeedData;
        if (data.seed === undefined || typeof data.seed === 'string' || data.isRandom === undefined) {
          const seed = parseInt(data.seed ?? data.result?.seed ?? data.output?.seed?.seed, 10);
          data.seed = Number.isFinite(seed) ? seed : 1;
          data.isRandom = data.isRandom ?? data.result?.isRandom ?? data.output?.seed?.isRandom ?? false;
        }
        break;
      }
      case 'router': {
        data = { inputNode: input.get('in') };
        break;
      }
      case 'multilora': {
        data = node.data as ImportLoraData;
        if (data.selectedIndex === undefined) {
          data.loras =
            data.loras?.map((l) => ({
              ...l,
              id: l.id ?? uuidv4(),
              defaultWeight:
                typeof l.defaultWeight === 'string' ? parseFloat(l.defaultWeight) : (l.defaultWeight ?? 0.5),
            })) ?? [];
          const index = data.loras.findIndex((l) => l.file === data.selectedLora?.file);
          data.selectedIndex = index && index >= 0 ? index : 0;
          data.weight = data.weight ?? 0.0;
          data.viewMode = NodeViewMode.All;
        }
        break;
      }
      case 'prompt_concat': {
        data = node.data as PromptConcatenatorData;
        data.inputNodes = Array.from(input.entries());
        if (data.additionalPrompt === undefined) {
          data.additionalPrompt = data.result?.additionalPrompt ?? '';
        }
        break;
      }
      case 'painterV2': {
        const DEFAULT_WIDTH = 426;
        const DEFAULT_HEIGHT = 426;

        data = node.data;
        const result = data.result as
          | {
              selectedSize: { width: number; height: number };
              scaleFactor: number;
              lockAspectRatio?: boolean;
              color: string;
              brushSize: number;
              backgroundColor: string;
              lines: { tool: 'brush' | 'eraser'; brushSize: number; color: string; points: number[] }[];
            }
          | undefined;

        data.inputNode = input.get('image');

        if (data.options === undefined && result !== undefined) {
          data.options = {
            width:
              result.selectedSize?.width && result.scaleFactor
                ? Math.round(result.selectedSize.width / result.scaleFactor)
                : DEFAULT_WIDTH,
            height:
              result.selectedSize?.height && result.scaleFactor
                ? Math.round(result.selectedSize.height / result.scaleFactor)
                : DEFAULT_HEIGHT,
            lockAspectRatio: result.lockAspectRatio ?? true,
            brushColor: result.color ? hexToRgbaObject(result.color) : { r: 255, g: 255, b: 255, a: 1 },
            brushSize: result.brushSize ? result.brushSize : 50,
            backgroundColor: result.backgroundColor
              ? hexToRgbaObject(result.backgroundColor)
              : { r: 0, g: 0, b: 0, a: 1 },
            lines: (result.lines ?? []).map((l) => {
              const points: Vec2[] = [];
              for (let i = 0; i < l.points.length; i += 2) {
                if (i + 1 >= l.points.length) break;
                points.push({ x: l.points[i], y: l.points[i + 1] });
              }
              return { ...l, color: hexToRgbaObject(l.color), points };
            }),
          };
        }

        if (data?.options !== undefined && data.options.lines !== undefined && data.options.strokes === undefined) {
          const width = data.options.width;
          const height = data.options.height;
          data.options.strokes = data.options.lines.map((l) => ({
            ...l,
            points: l.points.map((p) => ({ x: p.x - 0.5 * width, y: 0.5 * height - p.y })),
          }));
        }

        break;
      }
      case 'masks': {
        data = node.data as {
          result?:
            | Record<string, never>
            | { visualId: string }
            | {
                masks: Record<string, string>;
                selectedMasks: string[];
                panopticImage: string;
                visualId: string;
              };
        };

        let state: MaskExtractor['state'] = data.state;
        if (!state) {
          if (
            data.result?.visualId &&
            data.result?.masks &&
            data.result?.selectedMasks &&
            Array.isArray(data.result.selectedMasks) &&
            data.result?.panopticImage
          ) {
            function getMaskIndex(key: string): number | undefined {
              const maskIndexStr = key.match(/_(\d+).png$/)?.[1];
              if (maskIndexStr === undefined) return undefined;
              const maskIndex = Number(maskIndexStr);
              if (!Number.isFinite(maskIndex)) return undefined;
              return maskIndex;
            }

            const maskPairs = Object.entries(data.result.masks)
              .map(([key, url]) => {
                const maskIndex = getMaskIndex(key);
                return maskIndex !== undefined ? ([maskIndex, url] as const) : undefined;
              })
              .filter((pair) => pair != undefined);

            const maxIndex = Math.max(...maskPairs.map(([k]) => k));
            const masks = Array(maxIndex + 1).fill('');
            for (const [index, value] of maskPairs) {
              masks[index] = value;
            }

            state = {
              type: 'done',
              selectedMasks: data.result.selectedMasks.map(getMaskIndex).filter((m) => m != undefined),
              masks,
              panopticImage: data.result.panopticImage,
              visualId: data.result.visualId,
            };
          } else if (data.result?.visualId) {
            state = { type: 'image_registered', visualId: data.result.visualId };
          } else {
            state = { type: 'empty' };
          }
        }

        data = {
          image: input.get('image'),
          state,
          feather: migrateParameter(data, 'feather', { type: 'integer', value: 0 }),
          matte_choker: migrateParameter(data, 'matte_choker', { type: 'integer', value: 0 }),
          invert: { type: 'value', data: { type: 'boolean', value: false } },
          displayMode: 'image',
        } satisfies MaskExtractor;

        break;
      }
      case 'kling_element': {
        data = {
          frontalImageUrl: input.get('frontal_image_url'),
          referenceImageUrl1: input.get('reference_image_url1'),
          referenceImageUrl2: input.get('reference_image_url2'),
          referenceImageUrl3: input.get('reference_image_url3'),
        } satisfies KlingElement;
        break;
      }
      case 'custommodelV2': {
        data = node.data as AiModelData;
        const modelData = node.data as ModelBaseNodeData;
        const modelInfo: AiModelInfo =
          data.kind?.type === 'wildcard' && data.kind.model.type && data.kind.model.type !== 'none'
            ? data.kind.model
            : validateAiModel(modelData);
        if (data.generations) {
          for (const gen of data.generations) {
            if (gen?.kind?.type === 'audio') {
              if (isNil(gen.kind.duration)) {
                // This is a fallback for missing durations. Real migration is done on backend.
                // Before this - we didn't save duration on audio files
                gen.kind.duration = 5;
              }
            }
          }
        }
        switch (modelInfo.type === 'predefined' ? modelInfo.name : undefined) {
          case 'kling': {
            if (data.params?.model?.includes('3.0')) {
              data.kind = {
                type: 'kling',
                prompt: input.get('prompt'),
                image: input.get('image'),
                endImageUrl: input.get('end_image_url'),
                negativePrompt: input.get('negative_prompt'),
                elements: Array.from(input.entries()).filter(([key]) => key.startsWith('element_')),
                model: migrateParameter(modelData, 'model', { type: 'string', value: '3.0 Pro' }),
                duration: migrateParameter(modelData, 'duration', { type: 'integer', value: 5 }),
                cfgScale: migrateParameter(modelData, 'cfg_scale', { type: 'float', value: 0.5 }),
                shotType: migrateParameter(modelData, 'shot_type', { type: 'string', value: 'customize' }),
                aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: '16:9' }),
                generateAudio: migrateParameter(modelData, 'generate_audio', { type: 'boolean', value: false }),
              };
            } else {
              migrateWildcard(data, modelInfo);
            }
            break;
          }
          case 'Dalle3': {
            data.kind = {
              type: 'dalle3',
              text: input.get('text'),
            };
            break;
          }
          case 'minimax/image-01': {
            data.kind = {
              type: 'minimax_image01',
              prompt: input.get('prompt'),
              numberOfImages: migrateParameter(modelData, 'number_of_images', { type: 'integer', value: 1 }),
              aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: '1:1' }),
              promptOptimizer: migrateParameter(modelData, 'prompt_optimizer', { type: 'boolean', value: true }),
            };
            break;
          }
          case 'reve': {
            data.kind = {
              type: 'reve',
              prompt: input.get('prompt'),
              referenceImages: Array.from(input.entries()).filter(([key]) => key.startsWith('reference_image_')),
              aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: '1:1' }),
            };
            break;
          }
          case 'google/imagen-3': {
            data.kind = {
              type: 'imagen3',
              prompt: input.get('prompt'),
              negativePrompt: input.get('negative_prompt'),
              aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: '1:1' }),
              safetyFilterLevel: migrateParameter(modelData, 'safety_filter_level', {
                type: 'string',
                value: 'block_only_high',
              }),
            };
            break;
          }
          case 'wan-video/wan-2.1-1.3b': {
            data.kind = {
              type: 'wan_video',
              prompt: input.get('prompt'),
              aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: '16:9' }),
              frameNum: migrateParameter(modelData, 'frame_num', { type: 'integer', value: 81 }),
              resolution: migrateParameter(modelData, 'resolution', { type: 'string', value: '480p' }),
              sampleSteps: migrateParameter(modelData, 'sample_steps', { type: 'integer', value: 30 }),
              sampleGuideScale: migrateParameter(modelData, 'sample_guide_scale', { type: 'float', value: 6 }),
              sampleShift: migrateParameter(modelData, 'sample_shift', { type: 'float', value: 8 }),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 335386, isRandom: true } }),
            };
            break;
          }
          case 'br_bgreplace': {
            data.kind = {
              type: 'br_bgreplace',
              image: input.get('image'),
              referenceImage: input.get('reference_image'),
              backgroundPrompt: input.get('background_prompt'),
              fast: migrateParameter(modelData, 'fast', { type: 'boolean', value: true }),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { isRandom: true, seed: 42 } }),
              refinePrompt: migrateParameter(modelData, 'refine_prompt', { type: 'boolean', value: true }),
              originalQuality: migrateParameter(modelData, 'original_quality', { type: 'boolean', value: true }),
            };
            break;
          }
          case 'sd_sketch': {
            data.kind = {
              type: 'sd_sketch',
              image: input.get('image'),
              prompt: input.get('prompt'),
              controlType: migrateParameter(modelData, 'control_type', { type: 'string', value: 'Sketch' }),
              controlStrength: migrateParameter(modelData, 'control_strength', { type: 'float', value: 0.7 }),
            };
            break;
          }
          case 'luma/photon': {
            data.kind = {
              type: 'luma_photon',
              prompt: input.get('prompt'),
              imageReferenceUrl: input.get('image_reference_url'),
              styleReferenceUrl: input.get('style_reference_url'),
              characterReferenceUrl: input.get('character_reference_url'),
              aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: '16:9' }),
              imageReferenceWeight: migrateParameter(modelData, 'image_reference_weight', {
                type: 'float',
                value: 0.85,
              }),
              styleReferenceWeight: migrateParameter(modelData, 'style_reference_weight', {
                type: 'float',
                value: 0.85,
              }),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 335386, isRandom: true } }),
            };
            break;
          }
          case 'minimax/video-01-director': {
            data.kind = {
              type: 'minimax_video_director',
              prompt: input.get('prompt'),
              firstFrameImage: input.get('first_frame_image'),
              promptOptimizer: migrateParameter(modelData, 'prompt_optimizer', { type: 'boolean', value: true }),
            };
            break;
          }
          case 'meshy_image23d': {
            data.kind = {
              type: 'meshy',
              image: input.get('image'),
              surfaceMode: migrateParameter(modelData, 'surface_mode', { type: 'string', value: 'organic' }),
            };
            if (node.data.handles.output && !('image' in node.data.handles.output)) {
              node.data.handles.output['image'] = {
                description: 'Render of the 3d Model',
                label: 'Render',
                order: 1,
                type: 'image',
              };
            }
            break;
          }
          case 'tencent/hunyuan3d-2mv': {
            data.kind = {
              type: 'hunyuan3_d',
              frontImage: input.get('front_image'),
              backImage: input.get('back_image'),
              leftImage: input.get('left_image'),
              rightImage: input.get('right_image'),
              steps: migrateParameter(modelData, 'steps', { type: 'integer', value: 30 }),
              guidanceScale: migrateParameter(modelData, 'guidance_scale', { type: 'float', value: 5.0 }),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 1234, isRandom: true } }),
              octreeResolution: migrateParameter(modelData, 'octree_resolution', { type: 'integer', value: 256 }),
              removeBackground: migrateParameter(modelData, 'remove_background', { type: 'boolean', value: true }),
              numChunks: migrateParameter(modelData, 'num_chunks', { type: 'integer', value: 200000 }),
              randomizeSeed: migrateParameter(modelData, 'randomize_seed', { type: 'boolean', value: true }),
              targetFaceNum: migrateParameter(modelData, 'target_face_num', { type: 'integer', value: 10000 }),
            };
            break;
          }
          case 'black-forest-labs/flux-schnell': {
            data.kind = {
              type: 'flux_fast',
              prompt: input.get('prompt'),
              aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: '1:1' }),
              guidance: migrateParameter(modelData, 'guidance', { type: 'float', value: 3.5 }),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 176429, isRandom: true } }),
              outputFormat: migrateParameter(modelData, 'output_format', { type: 'string', value: 'png' }),
              outputQuality: migrateParameter(modelData, 'output_quality', { type: 'integer', value: 80 }),
            };
            break;
          }
          case 'black-forest-labs/flux-redux-dev': {
            data.kind = {
              type: 'flux_redux_dev',
              reduxImage: input.get('redux_image'),
              aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: '1:1' }),
              numOutputs: migrateParameter(modelData, 'num_outputs', { type: 'integer', value: 1 }),
              numInferenceSteps: migrateParameter(modelData, 'num_inference_steps', { type: 'integer', value: 28 }),
              guidance: migrateParameter(modelData, 'guidance', { type: 'float', value: 3.0 }),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 176429, isRandom: true } }),
              outputFormat: migrateParameter(modelData, 'output_format', { type: 'string', value: 'png' }),
              outputQuality: migrateParameter(modelData, 'output_quality', { type: 'integer', value: 80 }),
              disableSafetyChecker: migrateParameter(modelData, 'disable_safety_checker', {
                type: 'boolean',
                value: true,
              }),
              megapixels: migrateParameter(modelData, 'megapixels', { type: 'string', value: '1' }),
            };
            break;
          }
          case 'black-forest-labs/flux-canny-pro': {
            data.kind = {
              type: 'flux_canny_pro',
              prompt: input.get('prompt'),
              controlImage: input.get('control_image'),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 176429, isRandom: true } }),
              steps: migrateParameter(modelData, 'steps', { type: 'integer', value: 50 }),
              promptUpsampling: migrateParameter(modelData, 'prompt_upsampling', { type: 'boolean', value: false }),
              guidance: migrateParameter(modelData, 'guidance', { type: 'float', value: 30.0 }),
              safetyTolerance: migrateParameter(modelData, 'safety_tolerance', { type: 'integer', value: 6 }),
              outputFormat: migrateParameter(modelData, 'output_format', { type: 'string', value: 'jpg' }),
            };
            break;
          }
          case 'black-forest-labs/flux-depth-pro': {
            data.kind = {
              type: 'flux_depth_pro',
              prompt: input.get('prompt'),
              controlImage: input.get('control_image'),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 176429, isRandom: true } }),
              steps: migrateParameter(modelData, 'steps', { type: 'integer', value: 50 }),
              promptUpsampling: migrateParameter(modelData, 'prompt_upsampling', { type: 'boolean', value: false }),
              guidance: migrateParameter(modelData, 'guidance', { type: 'float', value: 10.0 }),
              safetyTolerance: migrateParameter(modelData, 'safety_tolerance', { type: 'integer', value: 2 }),
              outputFormat: migrateParameter(modelData, 'output_format', { type: 'string', value: 'png' }),
            };
            break;
          }
          case 'gpt_image_1': {
            if (!data.schema['model']) {
              data.schema['model'] = {
                type: 'enum',
                default: 'GPT Image 1.5',
                options: ['GPT Image 1.5', 'GPT Image 1'],
                exposed: false,
              };
            }
            data.kind = {
              type: 'gpt_image1',
              prompt: input.get('prompt'),
              model: migrateParameter(modelData, 'model', { type: 'string', value: 'GPT Image 1.5' }),
              size: migrateParameter(modelData, 'size', { type: 'string', value: '1024x1024' }),
              quality: migrateParameter(modelData, 'quality', { type: 'string', value: 'medium' }),
              background: migrateParameter(modelData, 'background', { type: 'string', value: 'opaque' }),
            };
            break;
          }
          case 'black-forest-labs/flux-1.1-pro': {
            data.kind = {
              type: 'flux11_pro',
              prompt: input.get('prompt'),
              imagePrompt: input.get('image_prompt'),
              aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: '1:1' }),
              width: migrateParameter(modelData, 'width', { type: 'integer', value: 256 }),
              height: migrateParameter(modelData, 'height', { type: 'integer', value: 256 }),
              safetyTolerance: migrateParameter(modelData, 'safety_tolerance', { type: 'integer', value: 6 }),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 1234, isRandom: true } }),
              promptUpsampling: migrateParameter(modelData, 'prompt_upsampling', { type: 'boolean', value: true }),
              outputFormat: migrateParameter(modelData, 'output_format', { type: 'string', value: 'png' }),
              outputQuality: migrateParameter(modelData, 'output_quality', { type: 'integer', value: 80 }),
            };
            break;
          }
          case 'black-forest-labs/flux-1.1-pro-ultra': {
            data.kind = {
              type: 'flux11_pro_ultra',
              prompt: input.get('prompt'),
              imagePrompt: input.get('image_prompt'),
              imagePromptStrength: migrateParameter(modelData, 'image_prompt_strength', { type: 'float', value: 0.1 }),
              aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: '1:1' }),
              safetyTolerance: migrateParameter(modelData, 'safety_tolerance', { type: 'integer', value: 2 }),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 1234, isRandom: true } }),
              raw: migrateParameter(modelData, 'raw', { type: 'boolean', value: false }),
              outputFormat: migrateParameter(modelData, 'output_format', { type: 'string', value: 'jpg' }),
            };
            break;
          }
          case 'flux-kontext': {
            data.kind = {
              type: 'flux_kontext',
              prompt: input.get('prompt'),
              image: input.get('image'),
              model: migrateParameter(modelData, 'model', { type: 'string', value: 'Dev' }),
              aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: 'match_input' }),
              numInferenceSteps: migrateParameter(modelData, 'num_inference_steps', { type: 'integer', value: 28 }),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 42, isRandom: true } }),
              guidanceScale: migrateParameter(modelData, 'guidance_scale', { type: 'float', value: 2.5 }),
              outputFormat: migrateParameter(modelData, 'output_format', { type: 'string', value: 'png' }),
            };
            break;
          }
          case 'flux-kontext-apps/multi-image-kontext-max': {
            data.kind = {
              type: 'flux_kontext_multi_image',
              prompt: input.get('prompt'),
              inputImage1: input.get('input_image_1'),
              inputImage2: input.get('input_image_2'),
              aspectRatio: migrateParameter(modelData, 'aspect_ratio', { type: 'string', value: 'match_input_image' }),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 1234, isRandom: true } }),
              outputFormat: migrateParameter(modelData, 'output_format', { type: 'string', value: 'png' }),
            };
            break;
          }
          case 'topaz-sharpen': {
            data.kind = {
              type: 'topaz_image_sharpen',
              image: input.get('image'),
              model: migrateParameter(modelData, 'model', { type: 'string', value: 'Standard' }),
              outputFormat: migrateParameter(modelData, 'output_format', { type: 'string', value: 'jpeg' }),
              subjectDetection: migrateParameter(modelData, 'subject_detection', { type: 'string', value: 'All' }),
              faceEnhancement: migrateParameter(modelData, 'face_enhancement', { type: 'boolean', value: true }),
              faceEnhancementCreativity: migrateParameter(modelData, 'face_enhancement_creativity', {
                type: 'float',
                value: 0.0,
              }),
              faceEnhancementStrength: migrateParameter(modelData, 'face_enhancement_strength', {
                type: 'float',
                value: 0.8,
              }),
            };
            break;
          }
          case 'fal-ai/topaz/upscale/image': {
            data.kind = {
              type: 'topaz_image_upscale',
              imageUrl: input.get('image_url'),
              model: migrateParameter(modelData, 'model', { type: 'string', value: 'Standard V2' }),
              upscaleFactor: migrateParameter(modelData, 'upscale_factor', { type: 'float', value: 2.0 }),
              cropToFill: migrateParameter(modelData, 'crop_to_fill', { type: 'boolean', value: false }),
              faceEnhancement: migrateParameter(modelData, 'face_enhancement', { type: 'boolean', value: true }),
              subjectDetection: migrateParameter(modelData, 'subject_detection', { type: 'string', value: 'All' }),
              faceEnhancementStrength: migrateParameter(modelData, 'face_enhancement_strength', {
                type: 'float',
                value: 0.8,
              }),
              faceEnhancementCreativity: migrateParameter(modelData, 'face_enhancement_creativity', {
                type: 'float',
                value: 0.0,
              }),
            };
            break;
          }
          case 'elevenlabs_voice_change': {
            data.kind = {
              type: 'eleven_labs_voice_changer',
              originVoice: input.get('origin_voice'),
              seed: migrateParameter(modelData, 'seed', { type: 'seed', value: { seed: 42, isRandom: true } }),
              voice: migrateParameter(modelData, 'voice', { type: 'string', value: 'Aria' }),
              removeBackgroundNoise: migrateParameter(modelData, 'remove_background_noise', {
                type: 'boolean',
                value: false,
              }),
            };
            break;
          }
          case 'any_llm': {
            // Detect which variant this is and migrate to the correct type
            const inputHandles = modelData.handles.input;
            const inputKeys = Array.isArray(inputHandles) ? inputHandles : Object.keys(inputHandles);

            // update schema to include current models
            data.schema.model.options = anyLlmModels();

            if (
              inputKeys.includes('system_prompt') &&
              inputKeys.includes('prompt') &&
              !('system_prompt' in modelData.schema) &&
              !('prompt' in modelData.schema)
            ) {
              // Any LLM - has both prompt and system_prompt
              if (!data.schema.thinking) {
                data.schema.thinking = {
                  type: 'boolean',
                  default: false,
                  required: false,
                  exposed: false,
                };
              }

              if (!data.schema.temperature) {
                data.schema.temperature = {
                  type: 'number',
                  max: 2,
                  min: 0,
                  title: 'Temperature',
                  default: 0,
                };
              }

              data.kind = {
                type: 'any_llm',
                prompt: input.get('prompt'),
                systemPrompt: input.get('system_prompt'),
                images: Array.from(input.entries()).filter(([key]) => key.startsWith('image')),
                model: migrateParameter(modelData, 'model', {
                  type: 'string',
                  value: 'google/gemini-2.0-flash-001',
                }),
                temperature: migrateParameter(modelData, 'temperature', { type: 'float', value: 0.0 }),
                thinking: migrateParameter(modelData, 'thinking', { type: 'boolean', value: false }),
              };
            } else if (inputKeys.includes('prompt') && !('prompt' in modelData.schema)) {
              // Prompt Enhancer - system prompt is a parameter
              data.kind = {
                type: 'prompt_enhancer',
                prompt: input.get('prompt'),
                systemPrompt: migrateParameter(modelData, 'system_prompt', {
                  type: 'string',
                  value:
                    'Your job is to write prompts for text-to-image models. Specifically for gaming assets. Your input will be a general description for the scene, You should write a prompt without any additions',
                }),
                images: Array.from(input.entries()).filter(([key]) => key.startsWith('image')),
                model: migrateParameter(modelData, 'model', {
                  type: 'string',
                  value: 'google/gemini-2.0-flash-001',
                }),
              };
            } else {
              // Image Describer - has only images

              if (modelData.schema.system_prompt) {
                modelData.schema.prompt = modelData.schema.system_prompt;
                delete modelData.schema.system_prompt;
              }
              if (modelData.params.system_prompt) {
                modelData.params.prompt = modelData.params.system_prompt;
                delete modelData.params.system_prompt;
              }

              if ('system_prompt' in modelData.handles.input) {
                modelData.handles.input.prompt = modelData.handles.input.system_prompt;
                delete modelData.handles.input.system_prompt;
                for (const edge of this.store.getEdges()) {
                  if (edge.target === node.id && edge.targetHandle) {
                    edge.targetHandle = edge.targetHandle.replace('system_prompt', 'prompt');
                  }
                }
              }

              for (const edge of this.store.getEdges()) {
                if (edge.target === node.id && edge.targetHandle && edge.targetHandle.endsWith('image_1')) {
                  edge.targetHandle = edge.targetHandle.replace(/image_1$/, 'image');
                }
              }

              data.kind = {
                type: 'image_describer',
                images: Array.from(input.entries()).filter(([key]) => key.startsWith('image')),
                model: migrateParameter(modelData, 'model', { type: 'string', value: 'google/gemini-2.0-flash-001' }),
                prompt: migrateParameter(modelData, 'prompt', {
                  type: 'string',
                  value: `You are an expert image analyst tasked with providing detailed accurate and helpful descriptions of images. Your goal is to make visual content accessible through clear comprehensive text descriptions. Be objective and factual using clear descriptive language. Organize information from general to specific and include relevant context. Start with a brief overview of what the image shows then describe the main subjects and setting. Include visual details like colors lighting, textures, style, genre, contrast and composition. Transcribe any visible text accurately. Use specific concrete language and mention spatial relationships. For people focus on actions clothing and general appearance respectfully. For data visualizations explain the information presented. Write as if describing to someone who cannot see the image including important context for understanding. Balance thoroughness with clarity and provide descriptions in natural flowing narrative form. Your description shouldn't be longer than 500 characters`,
                }),
              };
            }
            if (
              data.kind.model.type === 'value' &&
              data.kind.model.data.type === 'string' &&
              shouldBlockAnyLlmModel(data.kind.model.data.value)
            ) {
              // HOTFIX: remove anthropic models from any llm model for this client
              // Once we have an option to block certain models inside any_llm in the models management, we can remove this
              data.kind.model = {
                type: 'value',
                data: {
                  type: 'string',
                  value: 'google/gemini-2.0-flash-001',
                },
              };
            }
            data.schema.model.options = removeBlockedAnyLlmModelsIfNeeded(data.schema.model.options);
            break;
          }
          default: {
            migrateWildcard(data, modelInfo);
          }
        }

        if (
          data.generations === undefined ||
          data.generations.length !== (node.data.result as UploadedAsset[])?.length ||
          data.generations.some((f) => f.kind === undefined)
        ) {
          if (
            !Array.isArray(node.data.result) &&
            node.data.result &&
            typeof node.data.result === 'object' &&
            'type' in node.data.result
          ) {
            node.data.result = [node.data.result];
          }

          data.generations = ((node.data.result as UploadedAsset[]) ?? [])
            .map(UploadedAssetToModelAsset)
            .filter((asset) => asset !== undefined);
        }
        const filter = (i: number | undefined) => (i !== undefined && i >= 0 ? i : undefined);
        if (filter(data.selectedIndex) === undefined) {
          const selected =
            filter(data.selectedOutput) ?? data.generations.findIndex((f) => f.kind?.url === data.output?.file?.url);
          data.selectedIndex = filter(selected) ?? 0;
        }
        const selectedGeneration = data.generations[data.selectedIndex];
        data.params = mergeSeedParam(
          data.params,
          selectedGeneration ? ModelAssetToUploadedAsset(selectedGeneration) : undefined,
        );

        if (data.cameraLocked === undefined) {
          data.cameraLocked = data.cameraOptions?.locked ?? data.is3DLocked ?? false;
        }

        const cameraPosition = data.cameraOptions?.position ?? data.cameraPosition;
        if (
          cameraPosition != null &&
          cameraPosition.x != null &&
          cameraPosition.y != null &&
          cameraPosition.z != null
        ) {
          data.generations = data.generations.map((generation) => {
            if (generation.kind.type === '3D' && generation.kind.cameraOptions === undefined) {
              return {
                ...generation,
                kind: {
                  ...generation.kind,
                  cameraOptions: {
                    position: cameraPosition,
                    target: undefined,
                    up: { x: 0, y: 1, z: 0 },
                  },
                },
              };
            }
            return generation;
          });
        }

        break;
      }
      case 'point_cloud_from_depth': {
        data = node.data as PointCloudFromDepthData;
        if (data.options === undefined) {
          data.options = {
            depthScale: 5,
            minDepth: 0,
            maxDepth: 1,
            gammaDepth: 0.5,
          };
        }
        break;
      }
      // no need for migration
      case 'comparison':
      case 'deprecated': {
        data = node.data;
        break;
      }
      default: {
        const _exhaustiveCheck: never = type;
      }
    }
    return {
      kind: {
        type,
        data,
      } as NodeKind,
      locked: node.locked ?? this.getEffectiveIsLocked(node.id),
      parentId: node.parentId ?? undefined,
    };
  }

  getNodeType(nodeId: NodeId): NodeType {
    const node = this.store.getNode(nodeId);
    const type = node.type as NodeType;
    if (!type) {
      throw new Error(`Node ${nodeId} has no valid type: ${node.type}`);
    }
    return type;
  }

  getModelNodeType(nodeId: NodeId): ModelNodeType | undefined {
    return this.store.getNodeDataNullable<AiModelData>(nodeId)?.kind?.type;
  }
}

class EventHandler {
  constructor(
    private graph: FlowGraph,
    private canvas: HTMLCanvasElement,
  ) {
    document.body.addEventListener('pointermove', this.onPointerMove);
    document.body.addEventListener('pointerup', this.onPointerUp);
    document.body.addEventListener('pointerleave', this.onPointerLeave);
    document.body.addEventListener('pointerenter', this.onPointerEnter);
    document.body.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('wheel', this.onWheel);
  }

  onPointerMove = (e: PointerEvent) => {
    this.graph.wasm.call((wasm) => {
      wasm.onPointerMove(e);
    });
  };

  onPointerDown = (e: PointerEvent) => {
    this.graph.wasm.call((wasm) => {
      wasm.onPointerDown(e);
    });
  };

  onPointerUp = (e: PointerEvent) => {
    this.graph.wasm.call((wasm) => {
      wasm.onPointerUp(e);
    });
  };

  onPointerLeave = (e: PointerEvent) => {
    this.graph.wasm.call((wasm) => {
      wasm.onPointerLeave(e);
    });
  };

  onPointerEnter = (e: PointerEvent) => {
    this.graph.wasm.call((wasm) => {
      wasm.onPointerEnter(e);
    });
  };

  onWheel = (e: WheelEvent) => {
    this.graph.wasm.call((wasm) => {
      wasm.onWheel(e);
    });
  };

  dispose() {
    document.body.removeEventListener('pointermove', this.onPointerMove);
    document.body.removeEventListener('pointerup', this.onPointerUp);
    document.body.removeEventListener('pointerleave', this.onPointerLeave);
    document.body.removeEventListener('pointerenter', this.onPointerEnter);
    document.body.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('wheel', this.onWheel);
  }
}

export const handleTypeToDataType = (handleType: HandleType): DataType | undefined => {
  return validateDataType(handleType);
};

export const dataTypeToHandleType = (dataType: DataType | HandleType): HandleType => {
  switch (dataType) {
    case HandleType.Text:
    case 'text':
      return HandleType.Text;
    case HandleType.Number:
    case 'number':
      return HandleType.Number;
    case HandleType.Boolean:
    case 'boolean':
      return HandleType.Boolean;
    case HandleType.Array:
    case 'array':
      return HandleType.Array;
    case HandleType.Image:
    case 'image':
      return HandleType.Image;
    case HandleType.Video:
    case 'video':
      return HandleType.Video;
    case HandleType.Audio:
    case 'audio':
      return HandleType.Audio;
    case HandleType.ThreeDee:
    case '3D':
      return HandleType.ThreeDee;
    case HandleType.Seed:
    case 'seed':
      return HandleType.Seed;
    case HandleType.Any:
      return HandleType.Any;
    case HandleType.Lora:
    case 'lora':
      return HandleType.Lora;
    case HandleType.Mask:
    case 'mask':
      return HandleType.Mask;
    case HandleType.KlingElement:
    case 'kling-element':
      return HandleType.KlingElement;
    default: {
      const _unreachable: never = dataType;
      throw new Error(`Invalid data type: ${String(_unreachable as unknown)}`);
    }
  }
};
